/**
 * serial-checker-ui.js — browser glue for the serial checker.
 *
 * All decode logic lives in serial-decoder.js, which is byte-identical to the
 * unit-tested source. This file only handles DOM, URL state and rendering, so
 * a UI change can never alter a decode result.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ TO CHANGE THE WORDING, DO NOT EDIT THIS FILE.                        │
 * │                                                                      │
 * │ Every sentence the result card can say lives in                      │
 * │     src/decoder/ui-strings.json                                      │
 * │ with a per-brand override slot in each ruleset. Edit that, run       │
 * │     bun run checker:build                                            │
 * │ and reload. A test asserts no English sentence is left in here, so   │
 * │ if you find one to edit, that is a bug.                              │
 * │                                                                      │
 * │ Structure of a result card, all four parts optional:                 │
 * │     badgeKey / badgeValue  the big orange headline                   │
 * │     body                   one paragraph                             │
 * │     checks[]               the ticked list                           │
 * │     note                   the small print underneath                │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Vanilla ES5-compatible. Narrative 9.0.8 ships jQuery 3.7.1; we use none of it.
 */
(function () {
  "use strict";

  // ONE PAGE CAN CARRY MORE THAN ONE CHECKER.
  //
  // Gucci ships two: the leather-tab serial (which answers "which model?") and
  // the black fabric tag (which answers "which year?"). They read different
  // markings and answer different questions, so merging them into one box made
  // the page lie by omission — a black-tag code entered into a "serial" field
  // got a leather-tab explanation.
  //
  // Each panel is its own [data-serial-checker] root with its own state. This
  // used to be a single querySelector, so a second panel was silently dead.
  if (typeof SerialDecoder === "undefined") return;
  var roots = document.querySelectorAll("[data-serial-checker]");
  if (!roots.length) return;
  for (var ri = 0; ri < roots.length; ri++) initChecker(roots[ri]);
  initToolSwitch();
  initCtaTracking(roots[0].getAttribute("data-brand"));
  initTierCta();
  // After initTierCta: the preselected-tier sync reads the radios it normalises.
  initUploadPopup();

  // ---------------------------------------------------------------------------
  // ANALYTICS
  //
  // The live theme loads GTM (GTM-PN3LJZS) and initialises `window.dataLayer`
  // synchronously, then injects gtm.js 3s later — so a push that happens before
  // GTM arrives is queued on the array and replayed. That is the path that
  // lands in GA4. `gtag` and Shopify's own bus are attempted too, purely so
  // this keeps working if the tag stack is ever swapped; each is guarded and
  // none of them may throw into the decode path.
  //
  // ⚠️ WHAT IS NEVER SENT: the typed code. Not hashed, not truncated, not in a
  // page path. A serial identifies one physical item and often one person's
  // listing, and an analytics property is not the place for it. Only the brand
  // handle, the kind of answer shown, the era that answered, and a LENGTH
  // BUCKET — coarse on purpose, so a 12-character code is not distinguishable
  // from another 12-character code.
  // ---------------------------------------------------------------------------
  function track(name, params) {
    var p = params || {};
    // Channel of this visit on every checker event, so the GA4 funnel splits by
    // it. LGA is assigned further down, after the first panels initialise.
    try { if (p.lg_chan == null && typeof LGA !== "undefined" && LGA) p.lg_chan = LGA.chan(); } catch (e0) { /* as below */ }
    try {
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        var payload = { event: name };
        for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k)) payload[k] = p[k];
        window.dataLayer.push(payload);
      }
    } catch (e) { /* analytics must never break the tool */ }
    try {
      if (typeof window.gtag === "function") window.gtag("event", name, p);
    } catch (e2) { /* as above */ }
    try {
      var sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === "function") sa.publish(name, p);
    } catch (e3) { /* as above */ }
  }

  /**
   * Coarse length band. Never the length itself, never the value.
   *
   * ⚠️ FOLD BEFORE MEASURING. `[^0-9A-Za-z]` is an ASCII class, so a full-width
   * code — ４９３０７５, what a ja/ko/zh visitor's IME produces in wide mode —
   * had every character of it deleted and reported `"0"` for a code the tool
   * had just decoded correctly (round-2 QA defect 4). The decoder already folds
   * the same string before it reads it (`normalize()` calls `foldInput()`
   * first, unconditionally), so measuring the raw string measured something the
   * decode path never saw: same input, two answers.
   *
   * This is the ANALYTICS path, so the failure was silent and cosmetic — the
   * visible result was right throughout. That is exactly why it is worth
   * locking: a bucket that reads "0" for a whole class of visitor makes the
   * wide-input population look like it does not exist in any report, which is
   * the opposite of what this event was added to find out.
   *
   * `foldInput` is reached through `_internal` and guarded: if a future
   * decoder drops it, the bucket falls back to the old behaviour rather than
   * throwing inside a tracking call.
   */
  function lengthBucket(raw) {
    var s = String(raw == null ? "" : raw);
    try {
      if (SerialDecoder._internal && typeof SerialDecoder._internal.foldInput === "function") {
        s = SerialDecoder._internal.foldInput(s);
      }
    } catch (e) { /* analytics must never break the tool */ }
    var n = s.replace(/[^0-9A-Za-z]/g, "").length;
    if (!n) return "0";
    if (n <= 5) return "1-5";
    if (n <= 8) return "6-8";
    if (n <= 12) return "9-12";
    return "13+";
  }

  /**
   * One delegated listener for the whole page, installed once.
   *
   * The CTAs deliberately sit OUTSIDE the `[data-serial-checker]` panel — the
   * panel is just the tool, while the CTA lives in the limitation note and in
   * the FAQ bento — so a listener bound to the panel would never see them.
   * Bound to `document` instead, and only on a page that has a checker on it.
   *
   * Matching is on the PATHNAME, so a query string on the link (a variant id,
   * or a campaign tag on an external link) cannot hide a click from us. `[data-checker-cta]` is the
   * forward-looking hook: mark any new CTA with it and it is counted.
   */
  // ---------------------------------------------------------------------------
  // "WATCH | JEWELRY": A SWITCH BETWEEN PANELS. (Cartier, wave 4.)
  //
  // Only a section marked [data-sc-tools] has one; its value is the default
  // tool's id. Gucci's two stacked panels carry no such section, so nothing
  // below runs on any other page. The radio group picks the visible panel; a
  // `?tool=` link selects one on arrival, and the default tool never writes
  // the parameter, so a plain link to the page stays a plain link.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // CAMPAIGN PARAMETERS SURVIVE THE URL REWRITES. (Batch 2, Anton 2026-10-22.)
  //
  // The checker rewrites the URL twice: the tool switch writes `?tool=`, and a
  // lookup writes `?code=` (kept on purpose: Anton reads the codes in GA4).
  // Both used to write a bare pathname plus their own parameter, so a visitor
  // who arrived from an ad and checked a code before GTM loaded (it is injected
  // 3s after the page) lost gclid and the utm_* tags before the tag stack had
  // read them. These are carried over unchanged, after the checker's own.
  // ---------------------------------------------------------------------------
  var KEEP_PARAM = /^(gclid|gbraid|wbraid|fbclid|msclkid|utm_[A-Za-z0-9_]+)$/;
  function keptParams() {
    var kept = [];
    try {
      new URLSearchParams(location.search).forEach(function (v, k) {
        if (KEEP_PARAM.test(k)) kept.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
      });
    } catch (e) { /* an old browser without URLSearchParams#forEach keeps today's URL */ }
    return kept;
  }
  function urlWith(qs) {
    var all = qs.concat(keptParams());
    return location.pathname + (all.length ? "?" + all.join("&") : "");
  }

  function toolGroup(el) {
    for (var n = el; n && n.getAttribute; n = n.parentNode) {
      if (n.hasAttribute("data-sc-tools")) return n;
    }
    return null;
  }
  function activeTool(group) {
    var def = group.getAttribute("data-sc-tools");
    var want = new URLSearchParams(location.search).get("tool");
    if (want && group.querySelector('[data-serial-checker][data-tool="' + want.replace(/[^a-z0-9-]/gi, "") + '"]')) return want;
    return def;
  }
  // ---------------------------------------------------------------------------
  // STACKED PANELS SHARE ONE ?code=. (Batch 3x, Anton 2026-10-21.)
  //
  // Gucci and Omega stack two panels with no switch, and a link carries one
  // code. Restoring it into every panel put "not recognised" under the right
  // answer. Now `?tool=` names the one panel that restores, and without it
  // only the panels whose eras accept the code do (a silent decode: no render,
  // no event). If none accepts it, the first panel alone says so. A code that
  // fits two panels (an Omega reference) still answers in both, as before.
  // ---------------------------------------------------------------------------
  function stackedPanels(brand) {
    var all = document.querySelectorAll("[data-serial-checker][data-tool]");
    var list = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute("data-brand") === brand && !toolGroup(all[i])) list.push(all[i]);
    }
    return list;
  }
  function stackedRestores(root, params, rules) {
    var panels = stackedPanels(root.getAttribute("data-brand"));
    if (panels.length < 2) return true;
    var want = params.get("tool");
    for (var w = 0; want && w < panels.length; w++) {
      if (panels[w].getAttribute("data-tool") === want) return panels[w] === root;
    }
    var fits = [];
    for (var p = 0; p < panels.length; p++) {
      var only = (panels[p].getAttribute("data-eras") || "").split(",").filter(Boolean);
      var sub = {};
      for (var k in rules) if (Object.prototype.hasOwnProperty.call(rules, k)) sub[k] = rules[k];
      if (only.length) sub.eras = (rules.eras || []).filter(function (e) { return only.indexOf(e.id) !== -1; });
      var vals = {};
      var els = panels[p].querySelectorAll("input[name], select[name]");
      for (var e2 = 0; e2 < els.length; e2++) {
        var v = params.get(els[e2].name);
        if (v != null) vals[els[e2].name] = v;
      }
      try {
        if (!isMissState(SerialDecoder.decode(vals, sub).state)) fits.push(panels[p]);
      } catch (err) { /* a panel that cannot read it simply does not fit */ }
    }
    return fits.length ? fits.indexOf(root) !== -1 : panels[0] === root;
  }

  function showTool(group, id) {
    var radios = group.querySelectorAll('input[name="sc-tool"]');
    for (var i = 0; i < radios.length; i++) radios[i].checked = radios[i].value === id;
    var panels = group.querySelectorAll("[data-serial-checker][data-tool]");
    for (var j = 0; j < panels.length; j++) panels[j].hidden = panels[j].getAttribute("data-tool") !== id;
  }
  function initToolSwitch() {
    var groups = document.querySelectorAll("[data-sc-tools]");
    for (var g = 0; g < groups.length; g++) (function (group) {
      showTool(group, activeTool(group));
      group.addEventListener("change", function (ev) {
        var t = ev.target;
        if (!t || t.name !== "sc-tool") return;
        showTool(group, t.value);
        // The URL follows the switch, and drops a code that belonged to the
        // other panel, so a copied link opens what is on screen.
        if (window.history && window.history.replaceState) {
          var def = group.getAttribute("data-sc-tools");
          window.history.replaceState(null, "", urlWith(t.value === def ? [] : ["tool=" + encodeURIComponent(t.value)]));
        }
        track("checker_tool", { checker_brand: group.getAttribute("data-brand") || "", checker_panel: t.value });
      });
      // "Check it as jewelry": the card in one panel can hand its code to
      // another. The button carries only the target tool's id.
      group.addEventListener("click", function (ev) {
        var b = ev.target && ev.target.closest ? ev.target.closest("[data-sc-tool-go]") : null;
        if (!b) return;
        var id = b.getAttribute("data-sc-tool-go");
        var src = null;
        for (var n = b; n && n.getAttribute; n = n.parentNode) {
          if (n.hasAttribute("data-serial-checker")) { src = n; break; }
        }
        var dest = group.querySelector('[data-serial-checker][data-tool="' + id + '"]');
        if (!dest) return;
        var radio = group.querySelector('input[name="sc-tool"][value="' + id + '"]');
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
        var srcIn = src && src.querySelector('input[name="code"]');
        var destIn = dest.querySelector('input[name="code"]');
        if (srcIn && destIn) destIn.value = srcIn.value;
        if (dest.scRun) dest.scRun();
        if (destIn) destIn.focus();
      });
    })(groups[g]);
  }

  function initCtaTracking(brand) {
    document.addEventListener("click", function (ev) {
      var el = ev.target;
      while (el && el !== document) {
        if (el.nodeType === 1) {
          var hook = el.getAttribute && el.getAttribute("data-checker-cta");
          var cls = " " + (el.className && el.className.baseVal != null ? el.className.baseVal : (el.className || "")) + " ";
          var isKnown = cls.indexOf(" sc-limit__cta ") !== -1 ||
                        cls.indexOf(" product-v5-faq__bento-cell--cta ") !== -1;
          var href = el.tagName === "A" && el.getAttribute ? el.getAttribute("href") : null;
          if (hook != null || isKnown || isServiceLink(href)) {
            var ctaId = hook || (isKnown ? cls.replace(/\s+/g, " ").trim().split(" ").pop() : "product-link");
            track("checker_cta_click", {
              checker_brand: brand || "",
              cta_id: ctaId,
              cta_path: pathOf(href)
            });
            var tierEl = document.querySelector("input[data-sc-tier]:checked");
            markCart("cta", { checker_cta_id: ctaId, checker_tier: tierEl ? tierEl.value : "" });
            return;
          }
        }
        el = el.parentNode;
      }
    }, false);
  }

  // ---------------------------------------------------------------------------
  // THE TIMEFRAME CTA
  //
  // Two radio groups (inline block, phone bottom sheet) and two links share one
  // chosen variant. A change in either group re-checks the other and rewrites
  // `variant=` on both links, keeping any other query intact. The sheet opens from
  // the sticky bar and closes on backdrop, the x, or Escape. The bar hides
  // itself while the inline block is on screen.
  //
  // No text lives here: every word is in the generated markup.
  // ---------------------------------------------------------------------------
  function initTierCta() {
    var links = document.querySelectorAll("[data-sc-cta-link]");
    // A BRAND-PICKER page (paired with a category product, no answer-time
    // variants) has no tier links, but it does have the sticky bar, the sheet
    // of brand rows that bar opens, and the trustbox. Everything below the
    // variant sync works on it unchanged: there are no radios to sync.
    if (!links.length && !document.querySelector("[data-sc-picker]")) return;

    function setVariant(id) {
      for (var i = 0; i < links.length; i++) {
        var a = links[i];
        var parts = (a.getAttribute("href") || "").split("#")[0].split("?");
        var q = parts[1] ? parts[1].split("&") : [];
        var kept = [];
        for (var k = 0; k < q.length; k++) if (q[k].indexOf("variant=") !== 0) kept.push(q[k]);
        kept.unshift("variant=" + encodeURIComponent(id));
        a.setAttribute("href", parts[0] + "?" + kept.join("&"));
      }
      var radios = document.querySelectorAll("input[data-sc-tier]");
      var price = null;
      for (var r = 0; r < radios.length; r++) {
        radios[r].checked = radios[r].value === id;
        if (radios[r].checked && price === null) price = radios[r].getAttribute("data-price");
      }
      // The form's "Total Price" follows the chosen tier, as on the product page.
      var totals = document.querySelectorAll("[data-sc-total]");
      for (var t = 0; t < totals.length; t++) if (price) totals[t].textContent = price;
    }

    document.addEventListener("change", function (ev) {
      var t = ev.target;
      if (t && t.hasAttribute && t.hasAttribute("data-sc-tier") && t.checked) setVariant(t.value);
    }, false);

    var first = document.querySelector("input[data-sc-tier]:checked") || document.querySelector("input[data-sc-tier]");
    if (first) setVariant(first.value);

    var sheet = document.getElementById("sc-time-sheet");
    var bar = document.querySelector("[data-sc-sticky]");
    var opener = document.querySelector("[data-sc-sheet-open]");
    function openSheet() {
      if (!sheet) return;
      sheet.hidden = false;
      document.body.style.overflow = "hidden";
      if (opener) opener.setAttribute("aria-expanded", "true");
      var focus = sheet.querySelector("input[data-sc-tier]:checked") || sheet.querySelector("[data-sc-sheet-close]");
      if (focus && focus.focus) focus.focus();
    }
    function closeSheet() {
      if (!sheet || sheet.hidden) return;
      sheet.hidden = true;
      document.body.style.overflow = "";
      if (opener) { opener.setAttribute("aria-expanded", "false"); opener.focus(); }
    }
    document.addEventListener("click", function (ev) {
      var el = ev.target;
      while (el && el !== document) {
        if (el.hasAttribute) {
          if (el.hasAttribute("data-sc-sheet-open")) { openSheet(); return; }
          if (el.hasAttribute("data-sc-sheet-close")) { closeSheet(); return; }
        }
        el = el.parentNode;
      }
    }, false);
    document.addEventListener("keydown", function (ev) {
      if (ev.key === "Escape" || ev.keyCode === 27) closeSheet();
    }, false);

    var inline = document.querySelector("[data-sc-cta]");
    if (bar && inline && "IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        var on = entries[0].isIntersecting;
        if (on) bar.classList.add("is-hidden"); else bar.classList.remove("is-hidden");
      }, { threshold: 0.2 }).observe(inline);
    }

    initTrustbox();
  }

  // ---------------------------------------------------------------------------
  // UPLOAD PHOTOS -> THE THEME'S OWN PHOTO-UPLOAD POPUP
  //
  // Every "Upload Photos" control on a checker page is an <a> to
  // /products/<handle>?variant=<chosen tier>, written by the 297 generated brand
  // snippets. Those snippets are pasted to the live theme by hand, so the markup
  // is treated as frozen and the popup is wired from here instead: the dispatch
  // renders `photo-upload-fields` (the theme's real dialog, the same snippet the
  // v5 guides render) and this intercepts the click.
  //
  // PROGRESSIVE ENHANCEMENT IS THE WHOLE DESIGN. Four things have to be true
  // before a click is intercepted, and if any is false the anchor is left alone
  // and navigates to the product page exactly as it did before this existed:
  //
  //   .popup-overlay-u / .popup-container-u   the dialog was rendered at all
  //   .tabs-u has a .tab-section             the product HAS a configured upload
  //                                          form (metafields.custom.form_settings).
  //                                          An empty dialog is worse than the link.
  //   window.showJsAlert is a function       photo-upload-form-full.js actually
  //                                          loaded and ran. It assigns this on
  //                                          its FIRST line, so it is the cheapest
  //                                          honest "the script is here" signal —
  //                                          and the one that survives the asset
  //                                          being 404, blocked by the script
  //                                          blocker, or still in flight.
  //
  // Checked on every click rather than once at boot, because the asset is loaded
  // without defer but the blocker can release it later.
  //
  // THE CHOSEN TIER. photo-upload-form-full.js holds the variant in a script-local
  // `variantID` it refreshes on "change" from any element classed `product-form`,
  // a list it snapshots at load. The dispatch therefore renders a hidden relay
  // form with that class ahead of the script; we write the checked tier's id into
  // it and dispatch a bubbling change. `#selected-variant` is set too, but only
  // for honesty: the script reads it once, at init, and never again.
  //
  // No text here: nothing in this function is user-visible.
  // ---------------------------------------------------------------------------
  function initUploadPopup() {
    // The three CTA hooks the generated markup writes on an anchor. The phone
    // sticky bar's button carries `data-sc-sheet-open` instead when tiers exist
    // (it opens the sheet, and the sheet's own anchor is `sheet-cta`), so
    // `sticky-cta` here is the no-tier page's single-step bar.
    // `xcheck-cta` is round 3's conflict button inside "Does the code fit the
    // item?": the same upload, reached from the moment something did not add up.
    var HOOKS = { "tier-cta": 1, "sheet-cta": 1, "sticky-cta": 1, "xcheck-cta": 1 };

    function ready() {
      if (typeof window.showJsAlert !== "function") return null;
      var overlay = document.querySelector(".popup-overlay-u");
      var dialog = document.querySelector(".popup-container-u");
      var tabs = document.querySelector(".tabs-u");
      if (!overlay || !dialog || !tabs || !tabs.querySelector(".tab-section")) return null;
      return { overlay: overlay, dialog: dialog, tabs: tabs };
    }

    // The tier the visitor picked, preferring the group the clicked button sits
    // in (the bottom sheet has its own radios) and falling back to the page's.
    function pickedTier(btn) {
      var el = btn;
      while (el && el !== document) {
        if (el.nodeType === 1 && el.getAttribute &&
            (el.getAttribute("data-sc-cta") !== null || el.id === "sc-time-sheet")) {
          var own = el.querySelector("input[data-sc-tier]:checked");
          if (own) return own;
        }
        el = el.parentNode;
      }
      return document.querySelector("input[data-sc-tier]:checked") ||
             document.querySelector("input[data-sc-tier]");
    }

    // Hand the variant to photo-upload-form-full.js. Isolated in a try: a throw
    // here must not leave the visitor with a prevented click and no popup.
    function sendVariant(id) {
      if (!id) return;
      try {
        var hidden = document.getElementById("selected-variant");
        if (hidden) hidden.value = id;
        var relay = document.querySelector("[data-sc-upload-relay]");
        if (!relay) return;
        relay.value = id;
        var ev;
        if (typeof window.Event === "function") {
          ev = new Event("change", { bubbles: true });
        } else {
          ev = document.createEvent("HTMLEvents");
          ev.initEvent("change", true, false);
        }
        relay.dispatchEvent(ev);
      } catch (e) { /* the link still works */ }
    }

    document.addEventListener("click", function (ev) {
      if (ev.defaultPrevented || ev.button > 0 || ev.metaKey || ev.ctrlKey ||
          ev.shiftKey || ev.altKey) return;
      var el = ev.target, btn = null;
      while (el && el !== document) {
        if (el.nodeType === 1 && el.getAttribute && HOOKS[el.getAttribute("data-checker-cta")]) {
          btn = el; break;
        }
        el = el.parentNode;
      }
      if (!btn) return;
      var dom = ready();
      if (!dom) return;                       // fall through to the href

      var tier = pickedTier(btn);
      sendVariant(tier ? tier.value : "");

      ev.preventDefault();

      // The sheet and the dialog must not stack on a phone.
      var sheet = document.getElementById("sc-time-sheet");
      if (sheet && !sheet.hidden) {
        sheet.hidden = true;
        var opener = document.querySelector("[data-sc-sheet-open]");
        if (opener) opener.setAttribute("aria-expanded", "false");
      }

      dom.overlay.classList.remove("hidden");
      dom.overlay.classList.add("open");
      // 250ms: the theme's own opener uses 200 and the dialog's transition is
      // 350. Either reveals it after the overlay has painted; this matches the
      // v5 guide section, which is the behaviour visitors already see.
      window.setTimeout(function () { dom.dialog.classList.remove("transparent"); }, 250);
      var active = dom.tabs.querySelector(".tab-active");
      if (active) dom.tabs.style.height = active.clientHeight + "px";
      document.body.classList.add("popup-page");
      // The theme owns body scroll from here (`popup-page`), so release ours.
      document.body.style.overflow = "";
    }, false);

    // Sync the preselected tier once, so a visitor who never touches a radio
    // still buys the tier the page shows as chosen. Without this `variantID`
    // stays whatever `#selected-variant` held, which is the product's FIRST
    // variant in Shopify's own order — not the 30-minute default the radios
    // preselect. The same gap exists on the v5 guides.
    var first = document.querySelector("input[data-sc-tier]:checked");
    if (first) sendVariant(first.value);
  }

  /**
   * THE TRUSTPILOT BOOTSTRAP, ON DEMAND.
   *
   * The box it renders into (`sc-offer__box--trust`) is `display:none` below
   * 1024px, so on a phone this script was 8 KB fetched to paint nothing — and it
   * was fetched early, competing with the render-blocking stylesheets. The
   * generated markup now carries the URL in `data-sc-trustbox` (no literal here,
   * same rule as every string on this page) and this injects it once, only on a
   * viewport wide enough to show the box, and only after the offer block has
   * actually come into view.
   *
   * `matchMedia` is checked at INTERSECTION TIME, not at load: a tablet rotated
   * from portrait to landscape before the block scrolls in should still get it.
   * Where IntersectionObserver is missing the width test still applies and the
   * injection happens immediately — a browser without IO is not one to withhold
   * the widget from.
   */
  function initTrustbox() {
    var host = document.querySelector("[data-sc-trustbox]");
    if (!host) return;
    var url = host.getAttribute("data-sc-trustbox");
    if (!url) return;
    var done = false;

    function wide() {
      return !window.matchMedia || window.matchMedia("(min-width: 1024px)").matches;
    }
    function inject() {
      if (done || !wide()) return;
      done = true;
      var s = document.createElement("script");
      s.async = true;
      s.src = url;
      host.appendChild(s);
    }

    var block = document.querySelector("[data-sc-offer]") || host;
    if (!("IntersectionObserver" in window)) { inject(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      inject();
      if (done) io.disconnect();
    }, { rootMargin: "200px" });
    io.observe(block);
  }

  // ---------------------------------------------------------------------------
  // CART ATTRIBUTION
  //
  // The order itself must say it came from a checker, because GA4 cannot: a UTM
  // on an internal click would start a new session and overwrite the real
  // source (PMax, organic) in every dashboard, and the Shopify order journey
  // only knows a page was VISITED. So the page writes cart attributes through
  // Shopify's own /cart/update.js, which travel to the order as note
  // attributes and are readable in admin and by the API. Two marks:
  //   lg_used_at    — the visitor got an answer from the tool on this page
  //   lg_cta/_at    — the visitor clicked a checker CTA (plus the tier chosen)
  // Each with its page handle, the page locale and a timestamp, so the order
  // classifier (data/checkers/*/analyze.py) can tier them: CTA + that brand's
  // product within a day = the checker sold it; used but no CTA = almost sure;
  // journey only = touched. Same browser only — a cart is a cookie.
  // Never blocks the UI: fire-and-forget, keepalive so a navigating click
  // still delivers, and every failure is swallowed.
  // ---------------------------------------------------------------------------
  // LG-ATTR-BEGIN
  // WHERE THE VISITOR CAME FROM (2026-10-06). Shared BYTE FOR BYTE between
  // theme/assets/serial-checker-ui.js and
  // shopify-theme/sections/main-guide-v5.liquid; the test
  // src/__tests__/lgAttribution.test.ts fails if the two copies drift.
  //
  // It runs ONLY on checker and v5 guide pages, so it knows the entry page
  // only when the visit started on one of them. A visit that started elsewhere
  // reads as lg_chan "internal"; lg_gcl then says whether Google's own ad
  // cookie is present. Nothing here touches any other page of the store.
  //
  // What ends up on the order (all short strings, all prefixed lg_):
  //   lg_chan / lg_ref / lg_landing      this visit: channel, referrer host, entry path
  //   lg_clid_kind / lg_clid             ad click id type, and the id itself
  //   lg_gcl                             "1" when a _gcl_aw / _gcl_gb cookie exists
  //   lg_first_chan / _ref / _landing / _at   first touch, kept 90 days
  //   lg_path                            last five guide (g:) and checker (c:) pages
  //   lg_form_variant                    upload form variant, when the page names one
  //
  // CONSENT. The raw click id is an advertising identifier: it is stored and
  // sent only when Shopify's customerPrivacy API says marketing is allowed.
  // Browser storage (first touch, path) needs analytics to be allowed. Without
  // either, the channel of the CURRENT page is still classified in memory.
  // Channel "organic" is an upper bound: an ad click whose id was stripped by
  // the browser is indistinguishable from an organic one.
  var LGA = (function () {
    var FIRST_KEY = "lg_f", PATH_KEY = "lg_p", SESS_KEY = "lg_s";
    var DAY = 864e5, FIRST_TTL = 90 * DAY, PATH_TTL = 30 * DAY;
    var AI = /(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|copilot\.microsoft\.com|gemini\.google\.com|you\.com|phind\.com)$/;
    var SEARCH = /(^|\.)(google|bing|duckduckgo|yahoo|yandex|baidu|ecosia|naver|startpage|qwant)\.|(^|\.)search\.brave\.com$/;
    var SOCIAL = /(^|\.)(facebook|instagram|tiktok|pinterest|reddit|youtube|twitter|threads|linkedin|vk)\.|(^|\.)(t\.co|x\.com|youtu\.be)$/;
    var CLICK_IDS = ["gclid", "gbraid", "wbraid", "msclkid"];
    var state = { sess: null, first: null, path: null, clid: "", store: false };

    function param(search, name) {
      var m = new RegExp("[?&]" + name + "=([^&#]*)").exec(search || "");
      if (!m) return "";
      try { return decodeURIComponent(m[1].replace(/\+/g, " ")); } catch (e) { return m[1]; }
    }
    function hostOf(url) {
      var m = /^[a-z][a-z0-9+.-]*:\/\/([^\/?#:]+)/i.exec(url || "");
      return m ? m[1].toLowerCase() : "";
    }
    /** Pure: { search, referrer, host } -> { chan, ref, clid, clid_kind }. */
    function classify(env) {
      var s = env.search || "";
      var own = String(env.host || "").toLowerCase();
      var ref = hostOf(env.referrer);
      var kind = "", i;
      for (i = 0; i < CLICK_IDS.length && !kind; i++) if (param(s, CLICK_IDS[i])) kind = CLICK_IDS[i];
      var us = param(s, "utm_source").toLowerCase();
      var um = param(s, "utm_medium").toLowerCase();
      var chan;
      if (kind || /^(cpc|ppc|paid|paidsearch|paid_search|display|pmax)$/.test(um)) chan = "ads";
      else if (ref && ref === own) chan = "internal";
      else if (AI.test(ref) || AI.test(us)) chan = "ai";
      else if (um === "email" || us === "flow" || us === "klaviyo" || /^mail\./.test(ref)) chan = "email";
      else if (param(s, "fbclid") || param(s, "ttclid") || SOCIAL.test(ref) || um === "social") chan = "social";
      else if (SEARCH.test(ref)) chan = "organic";
      else if (ref) chan = "referral";
      else chan = us ? "campaign" : "direct";
      return { chan: chan, ref: ref === own ? "" : ref, clid: kind ? param(s, kind).slice(0, 200) : "", clid_kind: kind };
    }

    function allowed(what) {
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp) return what === "analytics";
        var fn = what === "marketing" ? cp.marketingAllowed : cp.analyticsProcessingAllowed;
        return typeof fn === "function" ? !!fn.call(cp) : what === "analytics";
      } catch (e) { return false; }
    }
    function read(kind, key) {
      try { return JSON.parse(window[kind].getItem(key) || "null"); } catch (e) { return null; }
    }
    function write(kind, key, val) {
      try { window[kind].setItem(key, JSON.stringify(val)); } catch (e) { /* private mode, quota */ }
    }
    function hasAdCookie() {
      try { return /(?:^|; )_gcl_(?:aw|gb)=/.test(document.cookie || ""); } catch (e) { return false; }
    }
    function formVariant() {
      try {
        var el = document.querySelector("[data-lg-form-variant]");
        return String((el && el.getAttribute("data-lg-form-variant")) || window.lgFormVariant || "").slice(0, 40);
      } catch (e) { return ""; }
    }

    /** Call once per page view. type is "guide" or "checker". */
    function visit(type, handle) {
      try {
        var now = Date.now();
        var here = classify({ search: location.search, referrer: document.referrer, host: location.hostname });
        here.landing = String(location.pathname || "").slice(0, 120);
        here.at = new Date(now).toISOString();
        // The consent API usually loads AFTER this script, and the checker
        // rewrites the URL on the first lookup, so the id is read now, held in
        // memory only, and released by attrs() once marketing is allowed.
        state.clid = here.clid;
        if (!allowed("marketing")) here.clid = "";
        var store = state.store = allowed("analytics");

        // This visit: the first of our pages seen in this tab. A fresh ad click
        // is a new visit, so it replaces whatever the tab remembered.
        var sess = store ? read("sessionStorage", SESS_KEY) : null;
        if (!sess || !sess.chan || here.clid_kind) {
          sess = here;
          if (store) write("sessionStorage", SESS_KEY, sess);
        }
        state.sess = sess;

        var first = store ? read("localStorage", FIRST_KEY) : null;
        if (!first || !first.chan || !(now - Date.parse(first.at) < FIRST_TTL)) {
          first = { chan: here.chan, ref: here.ref, landing: here.landing, at: here.at };
          if (store) write("localStorage", FIRST_KEY, first);
        }
        state.first = first;

        var p = store ? read("localStorage", PATH_KEY) : null;
        if (!p || !p.v || !p.v.length || !(now - p.t < PATH_TTL)) p = { t: now, v: [] };
        var step = (type === "guide" ? "g:" : "c:") + String(handle || "").slice(0, 60);
        if (p.v[p.v.length - 1] !== step) p.v.push(step);
        p.v = p.v.slice(-5);
        p.t = now;
        if (store) write("localStorage", PATH_KEY, p);
        state.path = p.v;
      } catch (e) { /* attribution must never break the page */ }
    }

    /** Every key is always present, so a later visit clears an earlier one's values. */
    function attrs() {
      var s = state.sess || {}, f = state.first || {};
      if (state.clid && s.clid_kind && !s.clid && allowed("marketing")) {
        s.clid = state.clid;
        if (state.store) write("sessionStorage", SESS_KEY, s);
      }
      return {
        lg_chan: s.chan || "",
        lg_ref: s.ref || "",
        lg_landing: s.landing || "",
        lg_clid_kind: s.clid_kind || "",
        lg_clid: (s.clid && allowed("marketing")) ? s.clid : "",
        lg_gcl: hasAdCookie() ? "1" : "",
        lg_first_chan: f.chan || "",
        lg_first_ref: f.ref || "",
        lg_first_landing: f.landing || "",
        lg_first_at: f.at || "",
        lg_path: (state.path || []).join(">"),
        lg_form_variant: formVariant()
      };
    }
    function chan() { return (state.sess && state.sess.chan) || ""; }

    return { classify: classify, visit: visit, attrs: attrs, chan: chan };
  })();
  // LG-ATTR-END
  try { LGA.visit("checker", pageHandle()); } catch (eVisit) { /* attribution must never break the tool */ }

  var cartMarked = {};
  // How many distinct answers this page view produced, and the kind of the last
  // one. Kinds only (the same vocabulary as checker_result), never the code.
  var lookups = 0;
  var lastKind = "";
  var usedAt = "";
  var USED_WRITES = 5;
  function pageHandle() {
    var parts = pathOf(window.location.pathname).split("/");
    return parts[parts.length - 1] || "";
  }
  /** The authentication product this page's CTAs sell, from the first product link. */
  /** The authentication product this page sells. `data-sc-product` is the
   *  page's own `sc_product`: on the four brand-picker pages the first
   *  /products/ link is a BRAND row, not the category product (plan §2 P2,
   *  2026-10-26). The first product link stays as the fallback (the hub). */
  function productHandle() {
    var own = document.querySelector("[data-sc-product]");
    var h = own ? String(own.getAttribute("data-sc-product") || "") : "";
    if (/^[a-z0-9][a-z0-9-]*$/.test(h)) return h;
    var a = document.querySelector('.sc-page a[href*="/products/"]');
    var m = a && /\/products\/([^/?#]+)/.exec(a.getAttribute("href") || "");
    return m ? m[1] : "";
  }
  function markCart(kind, extra) {
    // A CTA is written once. A result may be rewritten a few times so that
    // lg_lookups / lg_result_last follow a visitor who tries several codes.
    var done = cartMarked[kind] || 0;
    if (done >= (kind === "used" ? USED_WRITES : 1)) return;
    cartMarked[kind] = done + 1;
    try {
      // Shared key family with the v5 guides (main-guide-v5.liquid lgMark):
      // lg_src_type guide|checker, lg_src page handle, lg_src_group the
      // authentication product this page sells, lg_cta, lg_tier, lg_at, lg_locale.
      // `kind` keeps the two moments apart: lg_used_at for a real decode,
      // lg_cta/lg_cta_at for a click.
      var attrs = {
        lg_src_type: "checker",
        lg_src: pageHandle(),
        lg_src_group: productHandle(),
        lg_locale: (document.documentElement.getAttribute("lang") || "").toLowerCase()
      };
      var stamp = new Date().toISOString();
      if (kind === "used") { usedAt = usedAt || stamp; stamp = usedAt; }
      attrs["lg_" + kind + "_at"] = stamp;
      if (lookups) { attrs.lg_lookups = String(lookups); attrs.lg_result_last = lastKind; }
      var src = LGA.attrs();
      for (var sk in src) if (Object.prototype.hasOwnProperty.call(src, sk)) attrs[sk] = src[sk];
      if (kind === "cta") attrs.lg_cta = (extra && extra.checker_cta_id) || "cta";
      if (kind === "cta") attrs.lg_tier = (extra && extra.checker_tier) || "";
      if (kind === "used") attrs.lg_used = (extra && extra.checker_result_kind) || "decoded";
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      if (typeof window.fetch !== "function") return;
      window.fetch(root + "cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () { /* attribution must never break the tool */ });
    } catch (e) { /* as above */ }
  }

  /**
   * THE CROSS-CHECK OUTCOME ON THE ORDER (round 3). `lg_xcheck` is the overall
   * answer of "Does the code fit the item?" — fits, conflict or unclear, never
   * the code or the model — so the order classifier can tell whether a
   * conflict converts. Its own small write rather than a markCart kind: Shopify
   * merges cart attributes key by key, so this touches one key and leaves the
   * lg_used / lg_cta family exactly as markCart wrote it. Capped like the
   * `used` mark, and only when the answer changes.
   */
  var xcheckWrites = 0;
  var xcheckLast = "";
  function markXcheck(overall) {
    if (!overall || overall === xcheckLast || xcheckWrites >= USED_WRITES) return;
    xcheckLast = overall;
    xcheckWrites++;
    try {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      if (typeof window.fetch !== "function") return;
      window.fetch(root + "cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: { lg_xcheck: overall } })
      }).catch(function () { /* attribution must never break the tool */ });
    } catch (e) { /* as above */ }
  }

  /** The query string is dropped before comparing, so a UTM cannot mask it. */
  function pathOf(href) {
    if (!href) return "";
    return String(href).split("#")[0].split("?")[0];
  }

  function isServiceLink(href) {
    return pathOf(href).indexOf("/products/") !== -1;
  }

  /** Delegates to the decoder, which is where the script ranges are tested.
   *  Guarded so an older cached copy of the decoder asset cannot throw. */
  function isNonLatin(raw) {
    return typeof SerialDecoder.hasNonLatinLetters === "function"
      ? SerialDecoder.hasNonLatinLetters(raw)
      : false;
  }

  /**
   * The state, as one lowercase token for analytics. Deliberately its own map
   * rather than the raw state name: the four the reporting needs are `decoded`,
   * `format-only`, `not-recognised` and `non-latin`, and an engine rename must
   * not silently rename a GA4 dimension value a year of reports is built on.
   */
  function resultKind(state, raw) {
    var ST = SerialDecoder.STATE;
    if (state === ST.DECODED) return "decoded";
    if (state === ST.AMBIGUOUS) return "ambiguous";
    if (state === ST.FORMAT_OK_NO_DATE) return "format-only";
    if (state === ST.NO_CODE_ERA) return "no-code-era";
    if (state === ST.OUT_OF_SCOPE) return "out-of-scope";
    // Round 3: the two misses that used to hide inside INVALID_FORMAT. Their
    // own values, so a report can tell "the layout was right but the value was
    // not in our tables" from "we could not read it at all".
    if (ST.UNLISTED_VALUE && state === ST.UNLISTED_VALUE) return "unlisted-value";
    if (ST.IMPOSSIBLE_DATE && state === ST.IMPOSSIBLE_DATE) return "impossible-date";
    return isNonLatin(raw) ? "non-latin" : "not-recognised";
  }

  /**
   * A MISS: no reading at all. INVALID_FORMAT and, since round 3,
   * UNLISTED_VALUE and IMPOSSIBLE_DATE. The engine owns the list (isMiss); the
   * fallback is for an older cached decoder that predates it.
   */
  function isMissState(state) {
    return typeof SerialDecoder.isMiss === "function"
      ? SerialDecoder.isMiss(state)
      : state === SerialDecoder.STATE.INVALID_FORMAT;
  }

  function initChecker(root) {
  var brand = root.getAttribute("data-brand");
  var rulesEl = document.querySelector('script[data-serial-rules="' + brand + '"]');
  if (!rulesEl) return;

  var rules;
  try {
    rules = JSON.parse(rulesEl.textContent);
  } catch (e) {
    return; // leave the server-rendered table as the working fallback
  }

  // A panel may own only SOME of the brand's eras. `data-eras` lists them, and
  // the panel decodes against that subset only, so the black-tag box can never
  // answer with a leather-tab era and vice versa. No attribute means all eras,
  // which is what every other brand does.
  var fullRules = rules;
  var only = (root.getAttribute("data-eras") || "").split(",").filter(Boolean);
  if (only.length) {
    var subset = {};
    for (var rk in rules) if (Object.prototype.hasOwnProperty.call(rules, rk)) subset[rk] = rules[rk];
    subset.eras = (rules.eras || []).filter(function (e) { return only.indexOf(e.id) !== -1; });
    rules = subset;
  }

  // A SWITCH PANEL MAY SPEAK FOR ITSELF. Its tool can override result-card
  // sections (`strings`) and the brand's ended-code story (`codeEndedAt`,
  // null for "this marking never had one"): Cartier's 2009 watch guidance is
  // the wrong thing to tell someone holding a bracelet. Only switch panels
  // read these, and Gucci's tools carry neither key.
  //
  // A STACKED panel (Omega, Gucci: no switch) is still a tool with its own
  // marking, so its definition is found too, for `codeNoun` (see baseVars).
  // Before 2026-10-27 only switch panels looked theirs up, and Omega's
  // "Reference number" box answered a miss with "Omega serial number format".
  var group = toolGroup(root);
  var toolId = root.getAttribute("data-tool");
  var toolDef = null;
  if (toolId) {
    for (var ti = 0; ti < (fullRules.tools || []).length; ti++) {
      if (fullRules.tools[ti].id === toolId) toolDef = fullRules.tools[ti];
    }
  }
  if (toolDef && (toolDef.strings || Object.prototype.hasOwnProperty.call(toolDef, "codeEndedAt"))) {
    var own = {};
    for (var ok2 in rules) if (Object.prototype.hasOwnProperty.call(rules, ok2)) own[ok2] = rules[ok2];
    if (Object.prototype.hasOwnProperty.call(toolDef, "codeEndedAt")) own.codeEndedAt = toolDef.codeEndedAt;
    if (toolDef.strings) {
      var merged = {};
      var base = rules.strings || {};
      for (var bs in base) if (Object.prototype.hasOwnProperty.call(base, bs)) merged[bs] = base[bs];
      for (var ts in toolDef.strings) {
        if (!Object.prototype.hasOwnProperty.call(toolDef.strings, ts)) continue;
        var sec = {};
        var from = base[ts] || {};
        for (var fk in from) if (Object.prototype.hasOwnProperty.call(from, fk)) sec[fk] = from[fk];
        for (var tk in toolDef.strings[ts]) if (Object.prototype.hasOwnProperty.call(toolDef.strings[ts], tk)) sec[tk] = toolDef.strings[ts][tk];
        merged[ts] = sec;
      }
      own.strings = merged;
    }
    rules = own;
  }

  /** On a switch page, the other tool whose eras accept this code, or null. */
  function otherToolFor(vals) {
    if (!group || !fullRules.tools) return null;
    for (var oi = 0; oi < fullRules.tools.length; oi++) {
      var ot = fullRules.tools[oi];
      if (ot.id === toolId) continue;
      var sub = {};
      for (var sk2 in fullRules) if (Object.prototype.hasOwnProperty.call(fullRules, sk2)) sub[sk2] = fullRules[sk2];
      sub.eras = (fullRules.eras || []).filter(function (e) { return (ot.eras || []).indexOf(e.id) !== -1; });
      try {
        if (!isMissState(SerialDecoder.decode(vals, sub).state)) return ot;
      } catch (e) { /* a hint must never break the answer */ }
    }
    return null;
  }

  // There is no <form>. A typed code resolves on the "Check code" button or
  // Enter, not per keystroke (Anton, 2026-09-29: with the offer block under
  // the tool, a live-resolving field made the Upload Photos button read as the
  // checker's submit). Radios and selects still resolve on pick — a marker
  // question has nothing to "submit". `tool` is the element the controls live in.
  var tool = root.querySelector(".pv5-decoder__tool");
  var out = root.querySelector(".pv5-decoder__result");
  var S = SerialDecoder.STATE;

  // Last rendered card, so an identical re-render is skipped rather than
  // re-announced. See render().
  var lastHTML = "";
  var announceTimer = null;

  // Analytics de-duplication keys. Local only — the signature holds the typed
  // value and is never passed to track().
  var lastLookup = "";
  var lastResult = "";

  function signature(vals) {
    var parts = [];
    for (var sk in vals) if (Object.prototype.hasOwnProperty.call(vals, sk)) parts.push(sk + "=" + vals[sk]);
    return parts.sort().join("&");
  }

  /** The era that produced the answer, or "" — an id from the ruleset, never
   *  anything the visitor typed. */
  function answeringEra(res) {
    if (res.candidates && res.candidates.length && res.candidates[0].era) return res.candidates[0].era;
    if (res.eras && res.eras.length === 1) return res.eras[0].id;
    return "";
  }

  // Copy, shipped inside the ruleset by the build: ui-strings.json merged with
  // this brand's own `strings` override. `T` is deliberately terse because it
  // appears on every line of render().
  var STR = rules.strings || {};
  function T(section, key) {
    var block = STR[section] || {};
    var v = block[key];
    return v == null ? "" : v;
  }

  /**
   * Fill {placeholders}. An unknown key is LEFT AS IS rather than blanked, so a
   * typo in ui-strings.json shows up as a literal {tpyo} on the page instead of
   * a silent gap that nobody notices for a month.
   */
  function fill(tpl, vars) {
    if (tpl == null) return "";
    return String(tpl).replace(/\{(\w+)\}/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) && vars[key] != null
        ? String(vars[key])
        : whole;
    });
  }

  /** Escape, then fill — so a placeholder value cannot inject markup, while
   *  intentional markup in the template itself (e.g. <strong> around a factory)
   *  survives. */
  function fillEsc(tpl, vars) {
    var safe = {};
    for (var k in vars) if (Object.prototype.hasOwnProperty.call(vars, k)) safe[k] = esc(vars[k]);
    return fill(tpl, safe);
  }

  /**
   * The variables every string can use, regardless of state.
   *
   * `noun` is the brand's `codeNoun` unless the era that answered names its own.
   * One brand can carry two different markings: a Gucci is dated by the code on
   * its black fabric tag, not by the serial on the leather logo tab, and calling
   * that "this serial number" in the answer is simply wrong about which part of
   * the bag the reader is holding. An era only needs `codeNoun` when it differs
   * from the brand's, so nineteen rulesets are untouched.
   *
   * With no era to ask (a miss: not recognised, out of scope), the PANEL names
   * the marking: a tool's own `codeNoun` (Omega's reference box, Gucci's black
   * tag box) beats the brand's, so a miss in the reference box says "reference
   * number", not "serial number". A tool without one inherits the brand's.
   */
  //
  // `nounPlural` is English grammar: the noun plus "s". On a locale page the
  // noun is already translated, so the "s" printed "серийный номерs" and
  // "Referenznummers" (batch 3, 2026-10-21). There it is the noun unchanged,
  // and each locale's `checkNoDate` is worded to take a singular noun.
  var PAGE_EN = true;
  try { PAGE_EN = /^en\b/i.test(document.documentElement.getAttribute("lang") || "en"); } catch (e) { /* no lang: English */ }
  function baseVars(matchedEra) {
    var noun = (matchedEra && matchedEra.codeNoun) || (toolDef && toolDef.codeNoun) || rules.codeNoun;
    return {
      brand: rules.displayName,
      noun: noun,
      nounPlural: PAGE_EN ? noun + "s" : noun
    };
  }

  function fields() {
    return tool.querySelectorAll("input[name], select[name]");
  }

  // Leading "" so MONTHS[1] is January — the decoder returns 1-based months.
  var MONTHS = [""].concat((STR.describe && STR.describe.months) || []);

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  /** Programmatic rejection, for anyone who cannot see the card. Without this
   *  an unrecognised code was conveyed only as prose inside the live region. */
  function setInvalid(isInvalid) {
    var el = tool.querySelector('input[name="code"]');
    if (!el) return;
    if (isInvalid === null) el.removeAttribute("aria-invalid");
    else el.setAttribute("aria-invalid", isInvalid ? "true" : "false");
  }

  function readFields() {
    var f = {};
    var els = fields();
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      // Radio groups share a name, so only the checked one contributes. A
      // plain `f[name] = value` would let the last unchecked radio overwrite
      // the user's actual choice.
      if (el.type === "radio") {
        if (el.checked) f[el.name] = el.value;
      } else {
        f[el.name] = el.value;
      }
    }
    return f;
  }

  /** The full era object from the ruleset, by id. `res.eras` carries only
   *  id/label/sourceIds, and the readable half (tells, location, example)
   *  lives in the ruleset the page already ships. */
  function ruleEra(id) {
    if (!id) return null;
    for (var i = 0; i < (rules.eras || []).length; i++) {
      if (rules.eras[i].id === id) return rules.eras[i];
    }
    return null;
  }

  /** The label of the first enum input, i.e. the disambiguating question this
   *  page asks. Used to point at it by name rather than as "above". */
  function firstEnumLabel() {
    for (var i = 0; i < (rules.inputs || []).length; i++) {
      if (rules.inputs[i].type === "enum") return rules.inputs[i].label;
    }
    return null;
  }

  /**
   * True when the first question already has a real answer, so "Answer … to
   * narrow this" would ask for something the visitor gave. Empty and "unknown"
   * still count as open. "other" is Burberry's "Something else, or I can't
   * tell", an answer too (live check 2026-10-28: the card asked to pick a label
   * with one already picked).
   */
  function firstEnumAnswered() {
    var name = null;
    for (var i = 0; i < (rules.inputs || []).length; i++) {
      if (rules.inputs[i].type === "enum") { name = rules.inputs[i].name; break; }
    }
    if (!name) return false;
    var els = fields();
    for (var e = 0; e < els.length; e++) {
      if (els[e].name !== name || (els[e].type === "radio" && !els[e].checked)) continue;
      return !!els[e].value && els[e].value !== "unknown";
    }
    return false;
  }

  /**
   * THE ONE PLACE A YEAR IS COMPOSED.
   *
   * A candidate carries either `year` or a `yearFrom`/`yearTo` window, and both
   * have to come out as one finished piece of text that every sentence can drop
   * in unchanged. `describe.yearFormat` owns the single year, `describe.yearRange`
   * owns the window, and those two keys are the ONLY place a locale may put a
   * unit. Everything downstream interpolates the result verbatim.
   *
   * ⚠️ This used to be composed twice — once in describe() for the badge and
   * again in render() for `{year}` — so ja, whose yearRange ends in 年, got
   * `1987年～1988年年` the moment a check line added its own 年 (QA-locales D2).
   * One composition point is what makes "exactly one unit per year" a property
   * of the code rather than of every translated sentence agreeing.
   */
  function yearText(c) {
    if (c.yearFrom && c.yearTo) {
      return fill(T("describe", "yearRange"), { from: c.yearFrom, to: c.yearTo });
    }
    if (c.year) return oneYear(c.year);
    // AN OPEN WINDOW. A serial past the last published band (Omega 84M+, the
    // Speedmaster block from 77M) reads "from that year on" and nothing more,
    // and rendered as an empty badge. Each end is formatted as a single year
    // first, so the unit is still applied exactly once.
    if (c.yearFrom && T("crossCheck", "windowFrom")) return fill(T("crossCheck", "windowFrom"), { from: oneYear(c.yearFrom) });
    if (c.yearTo && T("crossCheck", "windowTo")) return fill(T("crossCheck", "windowTo"), { to: oneYear(c.yearTo) });
    return "";
  }

  /** One year, with the locale's unit. The only reader of `yearFormat`. */
  function oneYear(y) {
    return fill(T("describe", "yearFormat") || "{year}", { year: y });
  }

  /**
   * The factory/country value the ruleset emitted, as the visitor should read
   * it. The value is decoder data and stays what it is; `describe.countries`
   * carries the display name per locale, and an unlisted value renders raw
   * rather than blank (QA-locales D5).
   */
  /**
   * Round 4: the check line for a reading whose factory letters are in no
   * table (`res.unlistedPart`, set by the engine). Empty when there is none or
   * the locale has no string, so every other result renders exactly as before.
   */
  function unlistedLine(res, v) {
    if (!res || !res.unlistedPart || !T("decoded", "unlistedFactory")) return "";
    var w = {};
    for (var k in v) if (Object.prototype.hasOwnProperty.call(v, k)) w[k] = v[k];
    w.value = String(res.unlistedPart.value || "");
    return fillEsc(T("decoded", "unlistedFactory"), w);
  }

  function factoryText(value) {
    var map = T("describe", "countries");
    if (map && typeof map === "object" &&
        Object.prototype.hasOwnProperty.call(map, value) && map[value]) {
      return map[value];
    }
    return value;
  }

  function describe(c) {
    var bits = [];
    // A month and its year are ONE unit, because the order is not universal:
    // English writes "March 2020", ja writes 2020年3月. `describe.monthFormat`
    // owns that order; the English default is "{month} {year}", i.e. exactly
    // what the loose join below produced. A week-coded candidate keeps the old
    // month / week / year sequence — no ruleset carries all three.
    var monthName = c.month ? (MONTHS[c.month] || String(c.month)) : "";
    var monthFmt = T("describe", "monthFormat");
    var combined = monthName && c.year && monthFmt && !c.week;
    if (combined) {
      bits.push(fill(monthFmt, { month: monthName, monthNum: c.month, year: c.year }));
    } else if (monthName) {
      bits.push(monthName);
    }
    if (c.week) bits.push(T("describe", "weekPrefix") + " " + c.week);
    // A window, not a year. Rolex letter prefixes and the plateaus in its
    // numeric chart both land here: the data supports a span and saying one
    // year would be a confident guess. `combined` means monthFormat already
    // placed the year, so the bare-year path must not print it twice.
    if (c.yearFrom || c.yearTo || (c.year && !combined)) {
      var yt = yearText(c);
      if (yt) bits.push(yt);
    }
    var s = bits.join(" ");
    if (c.factory) s += fill(T("describe", "factorySuffix"), { factory: factoryText(c.factory) });
    return s || (c.label || "");
  }

  /**
   * Result tone. Deliberately three levels rather than a binary pass/fail:
   * calibrated hedging is more honest and reads as expertise. We never tell a
   * visitor their item "seems to be fake" — a code cannot support that claim,
   * and competitors who say it are overstating their own data.
   */
  /** The ticked list. Items arrive already escaped and already filled, so this
   *  only wraps them. An empty list renders nothing rather than an empty <ul>. */
  function li(items) {
    if (!items || !items.length) return "";
    return "<ul class=\"pv5-decoder__check\"><li>" + items.join("</li><li>") + "</li></ul>";
  }

  /**
   * One line per segment of a FORMAT-CHECK code, already escaped and filled.
   * Three shapes, all from ui-strings.json `formatCheck`: a value found in the
   * ruleset's table (`segmentKnown`, e.g. the model name), a value the table
   * does not list (`segmentUnknown`, never worded as a fault), and a part with
   * no table at all (`segmentPlain`, the part's own meaning).
   */
  function segmentLines(segs) {
    var out = [];
    if (!segs || !segs.length) return out;
    for (var i = 0; i < segs.length; i++) {
      var s = segs[i];
      var sv = { value: s.value, label: s.label, reading: s.reading || "",
                 meaning: s.meaning || "", note: s.note || "" };
      var key = s.known === true ? "segmentKnown"
        : s.known === false ? "segmentUnknown"
        : "segmentPlain";
      // A segment may carry the brand's own page for its value (G-Shock: Casio's
      // manual for the module number). Rendered only when the page ships a
      // `segmentLink` string, which only that ruleset does, so every other
      // page's card is unchanged. Inside the span, for the flex row below.
      var link = "";
      if (s.link && s.link.href && T("formatCheck", "segmentLink")) {
        link = fillEsc(T("formatCheck", "segmentLink"), { href: s.link.href, text: s.link.text || s.link.href });
      }
      // One span per line: the live .pv5-decoder__check li is a flex row, so a
      // bare <strong> would become its own flex item with a gap after it.
      out.push("<span>" + fillEsc(T("formatCheck", key), sv) + link + "</span>");
    }
    return out;
  }

  /**
   * Renders into the live component's own result card:
   *
   *   .pv5-decoder__badge  > __badge-k (mono label) + __badge-v (big orange value)
   *   .pv5-decoder__result-body
   *   .pv5-decoder__check  (checklist, orange ticks via li::before)
   *   .pv5-decoder__result-note
   *
   * Every one of those is already styled in v5-components.css, so nothing here
   * needs a new CSS rule.
   */
  function render(res, raw, view) {
    // Every branch fills the same four slots from ui-strings.json. Nothing
    // below writes a sentence; it only chooses which key and which variables.
    // The era that actually answered gets to name the marking, so a Gucci black
    // tag is not described to the reader as a serial number.
    //
    // `view` (round 3) is the cross-check NARROWING: an ambiguous code plus a
    // model, year or country that rules out every reading but one. The card
    // then shows that one reading as THE answer, labelled with what narrowed
    // it, and names the readings it ruled out. Analytics still report the
    // engine's own state: the narrowing is a view of the result, not a new one.
    var fullRes = res;
    if (view && view.cand) {
      res = { state: S.DECODED, candidates: [view.cand], eras: res.eras || [], notes: res.notes || [] };
    }
    var answering = (res.candidates && res.candidates.length && res.candidates[0].era)
      ? ruleEra(res.candidates[0].era)
      : (res.eras && res.eras.length === 1 ? ruleEra(res.eras[0].id) : null);
    var v = baseVars(answering);
    var badgeK = "", badgeV = "", alt = "", body = "", checks = [], note = "", route = "";

    if (res.state === S.DECODED) {
      var c = res.candidates[0];
      v.value = describe(c);
      // {year} has to survive a WINDOW, not just a single year. Rolex letter
      // prefixes, the plateaus in its numeric chart and every Omega band where
      // the two published charts disagree all return yearFrom/yearTo and no
      // `year`, which rendered the check line as "for a piece" — a sentence
      // with a hole in it, on the brands where hedging is the whole point.
      // Composed by yearText() and by nothing else, so the unit is applied once.
      v.year = yearText(c);
      v.era = c.eraLabel || "";
      badgeK = T("decoded", rules.codeNoun === "blind stamp" ? "badgeKeyYear" : "badgeKeyDate");
      badgeV = v.value;
      body = fillEsc(T("decoded", "body"), v);
      checks = (T("decoded", "checks") || []).map(function (t) { return fillEsc(t, v); });
      note = fillEsc(T("decoded", "note"), v);
      // A dated era that ALSO reads its code part by part (Seiko, 2026-10-21:
      // the caliber and the month character) names those parts first. Only
      // eras with a `segments` table produce `res.segments`, and until Seiko
      // every one of them was a no-date era, so no other page changes.
      checks = segmentLines(res.segments).concat(checks);
      // ROUND 4 (2026-10-29). The digits read as a date but the factory
      // letters are in no table (LV "AM2017"): say so first, so a dated card
      // is never read as "a recognised code". Never says fake.
      var unl = unlistedLine(fullRes, v);
      if (unl) checks.unshift(unl);
      // Critic pass (2026-10-29): a reading after the brand stopped printing
      // codes (LV "AR2221", week 22 of 2021) says so first. Brand string only.
      if (fullRes && fullRes.afterCodeEnded && T("decoded", "afterCodeEnded")) {
        checks.unshift(fillEsc(T("decoded", "afterCodeEnded"), v));
      }
      if (view && view.cand) {
        badgeK = T("crossCheck", view.by === "model" ? "narrowedByModel" : "narrowedByItem");
        alt = view.alt ? fill(T("crossCheck", "narrowedAlso"), { alt: view.alt }) : "";
      }

    } else if (res.state === S.AMBIGUOUS) {
      var years = res.candidates.map(describe);
      v.count = res.candidates.length;
      v.alt = years.slice(1).join(" " + T("ambiguous", "altPrefix") + " ");
      var q = firstEnumLabel();
      v.question = q || "";
      badgeK = T("ambiguous", "badgeKey");
      badgeV = years[0];
      alt = T("ambiguous", "altPrefix") + " " + v.alt;
      // The ruleset's own ambiguityNote is the brand-specific explanation and
      // beats the generic fallback whenever the brand supplies one.
      body = res.disambiguator ? esc(res.disambiguator) : fillEsc(T("ambiguous", "bodyFallback"), v);
      checks = (T("ambiguous", q ? "checks" : "checksNoQuestion") || []).map(function (t) { return fillEsc(t, v); });
      // A dated era that ALSO reads its code part by part (Seiko, 2026-10-21:
      // the caliber and the month character) names those parts first. Only
      // eras with a `segments` table produce `res.segments`, and until Seiko
      // every one of them was a no-date era, so no other page changes.
      checks = segmentLines(res.segments).concat(checks);
      note = fillEsc(T("ambiguous", res.candidates.length === 2 ? "noteTwo" : "noteMany"), v);

    } else if (res.state === S.FORMAT_OK_NO_DATE) {
      // Name the era that matched, and say what it means. Reporting only
      // "valid" made the disambiguating question pointless: the engine had
      // narrowed to one era and the card showed the same paragraph either way.
      var era = ruleEra(res.eras && res.eras.length === 1 ? res.eras[0].id : null);
      var many = res.eras && res.eras.length > 1;
      var q2 = firstEnumLabel();
      v.era = era ? era.label : "";
      v.tells = era ? era.tells || "" : "";
      v.location = era ? era.location || "" : "";
      // An era that describes a FAILING shape (Louis Vuitton's year after 2021,
      // batch 3) keeps that failing code as its `example`, which the tests hold
      // to the era. The card shows `cardExample` instead: a code that passes,
      // so the line never repeats the visitor's own mistake.
      v.example = era ? era.cardExample || era.example || "" : "";
      v.factory = res.factory ? factoryText(res.factory) : "";
      v.question = q2 || "";

      badgeK = T("formatOkNoDate", "badgeKey");
      badgeV = era ? esc(era.label) : T("formatOkNoDate", "badgeValueFallback");
      // notes[0] can be the engine's machine slug ("format-valid-no-date-encoded")
      // when a ruleset declares no prose note; a slug is not reader copy.
      var note = res.notes[0] && /\s/.test(res.notes[0]) ? res.notes[0] : "";
      body = v.tells ? esc(v.tells) : (note ? esc(note) : fillEsc(T("formatOkNoDate", "bodyFallback"), v));
      if (v.factory) body += fillEsc(T("formatOkNoDate", "factorySuffix"), v);

      checks = [];
      // FORMAT-CHECK: the code read part by part, first, because that is the
      // answer. Only rulesets with segment tables get a `segments` list, so
      // every other brand renders exactly as before.
      var segLines = segmentLines(res.segments);
      if (segLines.length) {
        checks = segLines;
        if (v.location) checks.push(fillEsc(T("formatOkNoDate", "checkLocation"), v));
        checks.push(fillEsc(T("formatCheck", "checkNoProof"), v));
        note = fillEsc(T("formatCheck", "note"), v);
      } else {
        if (v.location) checks.push(fillEsc(T("formatOkNoDate", "checkLocation"), v));
        if (v.example) checks.push(fillEsc(T("formatOkNoDate", "checkExample"), v));
        checks.push(fillEsc(T("formatOkNoDate", "checkNoDate"), v));
        if (many && q2 && !firstEnumAnswered()) checks.push(fillEsc(T("formatOkNoDate", "checkNarrow"), v));
        note = fillEsc(T("formatOkNoDate", era ? "note" : "noteFallback"), v);
      }

    } else if (res.state === S.NO_CODE_ERA) {
      badgeK = T("noCodeEra", "badgeKey");
      badgeV = fillEsc(T("noCodeEra", "badgeValue"), v);
      body = esc(res.guidance || "");
      checks = (T("noCodeEra", "checks") || []).map(function (t) { return fillEsc(t, v); });
      note = fillEsc(T("noCodeEra", "note"), v);

    } else if (res.state === S.OUT_OF_SCOPE) {
      badgeK = T("outOfScope", "badgeKey");
      badgeV = fillEsc(T("outOfScope", "badgeValue"), v);
      body = fillEsc(T("outOfScope", "body"), v);
      checks = (T("outOfScope", "checks") || []).map(function (t) { return fillEsc(t, v); });
      note = fillEsc(T("outOfScope", "note"), v);
      route = routeHtml();

    } else if (S.UNLISTED_VALUE && res.state === S.UNLISTED_VALUE && STR.unlistedValue) {
      // ROUND 3. The layout matched, a value inside it is not in our tables.
      // Three shapes of sentence: a value missing from a table (an LV factory
      // "ZZ"), a year outside the years the layout ran, and a year after the
      // brand stopped printing codes at all, where the brand's own guidance —
      // LV's "small unlined pieces kept a printed code" — is the likeliest
      // explanation and is shown in full.
      var un = res.unlisted || {};
      v.value = typeof un.value === "number" ? yearText({ year: un.value }) : String(un.value == null ? "" : un.value);
      v.span = windowText(un.eraFrom, un.eraTo);
      v.guidance = res.guidance || "";
      var unKey = "body";
      if (un.afterCodeEnded && res.guidance) unKey = "bodyAfterEnded";
      else if (un.reason === "outside-era" && v.span) unKey = "bodyOutsideEra";
      badgeK = T("unlistedValue", "badgeKey");
      badgeV = fillEsc(T("unlistedValue", "badgeValue"), v);
      body = fillEsc(T("unlistedValue", unKey), v);
      checks = (T("unlistedValue", "checks") || []).map(function (t) { return fillEsc(t, v); });
      note = fillEsc(T("unlistedValue", "note"), v);

    } else if (S.IMPOSSIBLE_DATE && res.state === S.IMPOSSIBLE_DATE && STR.impossibleDate) {
      // ROUND 3. A date that cannot exist: after today, a month 13, a week 54.
      var im = res.impossible || {};
      v.month = im.month != null ? im.month : (im.value != null ? im.value : "");
      v.week = im.week != null ? im.week : (im.value != null ? im.value : "");
      v.when = describe({ year: im.year, month: im.reason === "future" ? im.month : null,
                          week: im.reason === "future" ? im.week : null });
      var imKey = im.reason === "invalid-month" ? "bodyMonth" : im.reason === "invalid-week" ? "bodyWeek" : "bodyFuture";
      badgeK = T("impossibleDate", "badgeKey");
      badgeV = fillEsc(T("impossibleDate", "badgeValue"), v);
      body = fillEsc(T("impossibleDate", imKey), v);
      checks = (T("impossibleDate", "checks") || []).map(function (t) { return fillEsc(t, v); });
      note = fillEsc(T("impossibleDate", "note"), v);

    } else {
      // INVALID_FORMAT. Never says or implies "fake" — the data cannot support
      // it, and a competitor who says it is overstating their own dataset.
      //
      // TWO DIFFERENT SITUATIONS, one state. If what was typed contains letters
      // from a script no brand prints on an item — a ja/ko/zh IME left in place,
      // the brand name written in the reader's own language, a Cyrillic layout —
      // then "we have nothing on record matching that" is the wrong sentence:
      // there was nothing to match. The `nonLatinInput` block says so and asks
      // for the characters as printed. It is a separate section in
      // ui-strings.json, and if a ruleset was built before that section existed
      // we fall back to the old copy rather than render an empty card.
      var nonLatin = isNonLatin(raw) && !!(STR.nonLatinInput && STR.nonLatinInput.body);
      var sect = nonLatin ? "nonLatinInput" : "invalidFormat";
      badgeK = T(sect, "badgeKey");
      badgeV = fillEsc(T(sect, "badgeValue"), v);
      body = fillEsc(T(sect, "body"), v);
      // THE MOST LIKELY EXPLANATION, when the brand stopped issuing codes.
      //
      // The engine already works this out and attaches `guidance` — Chanel
      // moved to a plate and a chip in 2021, Louis Vuitton to a chip in March
      // 2021 — and the card was throwing it away. So the single most common
      // real reason a code does not resolve, that the item is simply newer than
      // the code system, was computed and never shown. "Not recognised" is a
      // bad answer to give someone holding a perfectly ordinary new bag.
      // Only when we could read the characters at all. Telling someone whose
      // input we could not read that their item is probably too new to carry a
      // code would be an answer we have not earned.
      if (res.guidance && !nonLatin) {
        v.guidance = res.guidance;
        body += " " + fillEsc(T("invalidFormat", "endedLead"), v);
      }
      // FORMAT-CHECK: the shape was right but one part holds a value the brand
      // does not print there. Name the part, so "not recognised" points at
      // the character to re-read rather than at the whole code.
      var rej = res.rejectedSegment;
      // "M, W, J or K": the separator and the last joiner are both strings,
      // because neither is punctuation every language shares.
      var listJoin = function (a) {
        return a.length < 3 ? a.join(T("formatCheck", "allowedJoin"))
          : a.slice(0, -1).join(T("formatCheck", "allowedSep")) + T("formatCheck", "allowedJoin") + a[a.length - 1];
      };
      if (rej && !nonLatin && T("formatCheck", "rejected")) {
        body += " " + fillEsc(T("formatCheck", "rejected"), {
          brand: v.brand, label: rej.label, value: rej.value,
          allowed: listJoin(rej.allowed || [])
        });
      }
      checks = (T(sect, "checks") || []).map(function (t) { return fillEsc(t, v); });
      note = fillEsc(T(sect, "note"), v);
      // On a switch page, a code this panel refuses may be exactly what the
      // OTHER panel reads (a Love-bracelet serial typed into the watch box,
      // which is the default). Say so, and offer to move it across.
      var other = !nonLatin && T("invalidFormat", "otherTool") ? otherToolFor({ code: raw }) : null;
      // The ended-code guidance and the typo checks are about THIS panel's
      // marking, so they give way to the one sentence that applies.
      if (other) {
        var ov = { brand: v.brand, tool: other.label };
        if (T("invalidFormat", "otherToolBadge")) badgeV = fillEsc(T("invalidFormat", "otherToolBadge"), ov);
        body = fillEsc(T("invalidFormat", "otherTool"), ov) +
          ' <button type="button" class="sc-check" data-sc-tool-go="' + esc(other.id) + '">' +
          fillEsc(T("invalidFormat", "otherToolButton"), ov) + "</button>";
        checks = [];
      }
      if (!other && !nonLatin) route = routeHtml();
    }

    var html =
      '<div class="pv5-decoder__badge">' +
        '<span class="pv5-decoder__badge-k">' + esc(badgeK) + "</span>" +
        '<span class="pv5-decoder__badge-v">' + badgeV +
          (alt ? ' <span class="pv5-decoder__badge-alt' + (view && view.cand ? " sc-badge-alt--line" : "") + '">' + esc(alt) + "</span>" : "") +
        "</span>" +
      "</div>" +
      // The chip goes first on a miss: it is the one thing the reader can act
      // on, and some brands' explanations below it run to twenty lines.
      suggestHtml(fullRes) +
      '<p class="pv5-decoder__result-body">' + body + "</p>" +
      li(checks) +
      '<p class="pv5-decoder__result-note">' + note + "</p>" +
      route +
      guideHtml(fullRes);

    // NOTHING CHANGED, SO SAY NOTHING. This card lives in a role="status"
    // aria-live region with aria-atomic="true", so every write re-reads the
    // whole ~60-word result. A radio fires `input` AND `change`, and typing
    // fires once per character, which had a seven-character serial announcing
    // the full card eight times over. Screen-reader users could not use the
    // tool at all.
    if (html === lastHTML) return;
    lastHTML = html;

    // UNHIDE BEFORE WRITING. `[hidden]` is `display:none` in v5-components.css,
    // and mutations inside a display:none region are not announced — nor is
    // unhiding an already-populated one, reliably. Written in this order the
    // FIRST result is announced; in the other order it was silent.
    out.hidden = false;
    out.innerHTML = html;
    // The Gucci model box lives inside the card; a rewrite of the card (a
    // cross-check narrowing, a guide link changing) must not drop it.
    if (modelCode) paintModel(modelCode);
    // Next frame, so the transition has a start state to animate from.
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () { out.classList.add("is-visible"); });
    } else {
      out.classList.add("is-visible");
    }
  }

  // ---------------------------------------------------------------------------
  // EMPTY SUBMIT. (Batch 2, Anton 2026-10-22, option A.)
  //
  // Pressing Check (or Enter) with an empty field, or one holding only spaces,
  // used to do nothing at all. Now a short line under the field says to type
  // the code first and shows this page's own example: the input's placeholder,
  // which every ruleset and locale already writes ("e.g. R123456 or 5100000").
  // Only an explicit submit shows it; clearing the field just hides it again.
  // A page whose code is optional (Fendi, hallmark) never shows it.
  // The sentence is ui-strings.json `emptyInput.note`, like every other one.
  // ---------------------------------------------------------------------------
  function codeRequired() {
    var ins = rules.inputs || [];
    for (var i = 0; i < ins.length; i++) if (ins[i].name === "code") return ins[i].required !== false;
    return false;
  }
  function emptyLine(show) {
    var input = tool.querySelector('input[name="code"]');
    if (!input) return;
    var line = tool.querySelector("[data-sc-empty]");
    if (!show) {
      if (line && !line.hidden) {
        line.hidden = true;
        input.removeAttribute("aria-describedby");
      }
      return;
    }
    var text = fill(T("emptyInput", "note"), {
      brand: rules.displayName,
      noun: baseVars().noun,
      example: input.getAttribute("placeholder") || ""
    });
    if (!text) return;
    if (!line) {
      line = document.createElement("p");
      line.className = "sc-empty";
      line.setAttribute("data-sc-empty", "");
      line.setAttribute("role", "alert");
      line.id = (input.id || "sc-code") + "-empty";
      var field = input.closest ? input.closest(".pv5-decoder__field") : null;
      (field || input.parentNode).appendChild(line);
    }
    line.textContent = text;
    line.hidden = false;
    input.setAttribute("aria-describedby", line.id);
  }

  /** True once every input the ruleset marks `required` has a value. */
  function ready(vals) {
    var ins = rules.inputs || [];
    var sawRequired = false;
    for (var i = 0; i < ins.length; i++) {
      if (!ins[i].required) continue;
      sawRequired = true;
      if (!vals[ins[i].name]) return false;
    }
    // A ruleset with no required input at all still needs something to go on.
    return sawRequired || !!vals.code;
  }

  function run(push, submitted) {
    // Named `vals`, not `fields` — `fields` is the element-list helper above,
    // and a local of the same name would shadow it inside this function.
    var vals = readFields();
    // A required code that is empty or only spaces is not a lookup. Before
    // batch 2 a spaces-only code reached the engine and came back as "Not
    // recognized"; now it is treated exactly like an empty field.
    var blankCode = codeRequired() && !String(vals.code == null ? "" : vals.code).replace(/\s+/g, "");
    emptyLine(blankCode && submitted === true);
    // WAIT FOR THE FIRST REQUIRED INPUT, whichever it is.
    //
    // This used to require `code`, which assumed every checker is driven by a
    // typed serial. Fendi is not: its answer comes from a marker question and
    // the serial is optional, so the page sat silent no matter what you picked.
    // The ruleset already says which inputs are required, so ask it.
    if (blankCode || !ready(vals)) {
      out.hidden = true;
      out.classList.remove("is-visible");
      lastHTML = "";           // so the next real result is not suppressed
      setInvalid(null);
      lastRes = null;
      showExtras(null);
      return;
    }
    // An example chip (round 3) is a demonstration, not a lookup: its events
    // say so, and it never marks the cart or counts as a use.
    var isEx = fromExample;
    // ONE LOOKUP PER SETTLED CODE, NOT ONE PER KEYSTROKE. `run` arrives from
    // the 450ms debounce, but a pause mid-serial still fires it, so the inputs
    // themselves are the key. `push` is false only when we are restoring a
    // shared link, which is a result view and not a lookup the visitor made.
    var key = signature(vals);
    if (push && key !== lastLookup) {
      lastLookup = key;
      track("checker_lookup", exampleFlag({
        checker_brand: brand,
        checker_panel: root.getAttribute("data-tool") || "",
        code_length: lengthBucket(vals.code)
      }, isEx));
    }

    var res = SerialDecoder.decode(vals, rules);
    setInvalid(isMissState(res.state));
    lastRes = res;
    lastRaw = vals.code;
    lastRunExample = isEx;
    // A new code is a new answer: the model index hit belonged to the last one.
    modelHit = null;
    modelCode = "";
    renderMain();

    // AFTER render, so the event describes what the visitor can actually see.
    var kind = resultKind(res.state, vals.code);
    if (key + "|" + kind !== lastResult) {
      lastResult = key + "|" + kind;
      track("checker_result", exampleFlag({
        checker_brand: brand,
        checker_panel: root.getAttribute("data-tool") || "",
        result_kind: kind,
        era_id: answeringEra(res),
        code_length: lengthBucket(vals.code)
      }, isEx));
      // Only a real answer marks the cart. A miss — INVALID_FORMAT and, since
      // round 3, UNLISTED_VALUE and IMPOSSIBLE_DATE — is not "used", and an
      // example chip is not the visitor's own code.
      var counts = push && !isEx && !isMissState(res.state);
      if (counts) { lookups = (lookups || 0) + 1; lastKind = kind; }
      if (counts) markCart("used", { checker_result_kind: kind });
    }

    lookupModel(vals.code, res);

    // Shareable result URL, in the QUERY STRING: the live decision of
    // batch 2 (2026-10-22) is that a lookup writes ?code= on purpose,
    // because Anton reads the codes in GA4. Round 3 (2026-10-26) had moved
    // the code into the fragment to keep it out of page_location; the
    // live decision wins until Anton says otherwise. What round 3 adds is
    // kept: the cross-check answers (model, country, year, pair code,
    // features) ride in the FRAGMENT after it, so they never reach
    // analytics, and the share button copies the same URL. resultQs().
    if (push) writeResultUrl();
  }

  // ===========================================================================
  // ROUND 3 (2026-10-26): example chips, "Did you mean" chips, the guide link
  // under a result, the share link, and "Does the code fit the item?".
  //
  // Same rules as the rest of this file: every word comes from ui-strings.json
  // (STR), every value placed into markup goes through esc(), and nothing the
  // visitor typed is ever passed to track(). Each builder is wrapped so a
  // failure in an optional extra can never take the decoder down with it.
  // ===========================================================================
  var lastRes = null;
  var lastRaw = "";
  var lastRunExample = false;
  var fromExample = false;
  // What Gucci's model index said about the current code: the design name, or
  // null. Handed to crossCheck() as `modelIndexHit`; the engine never fetches.
  var modelHit = null;
  var XO = null;
  try {
    if (typeof SerialDecoder.crossCheckOptions === "function") XO = SerialDecoder.crossCheckOptions(rules);
  } catch (eXo) { XO = null; }
  var xcOn = !!(XO && XO.fields && XO.fields.length && typeof SerialDecoder.crossCheck === "function" && STR.crossCheck);
  var xcPanel = null;
  var actionsEl = null;
  var shareStatus = null;
  var shareTimer = null;
  var xcInputTimer = null;
  var xcEventTimer = null;
  var xcSent = "";
  var xcHtml = "";
  var xcCommittedModel = false;
  var stickyEl = null;
  var stickyText = null;
  // One pair-code value, or a map of them: the ruleset declares either a single
  // pair check or a list, and crossCheck() reads the item in the same shape.
  var pairListMode = !!(rules.crossCheck && Array.isArray(rules.crossCheck.pairCode));

  function has(field) { return !!(XO && XO.fields && XO.fields.indexOf(field) !== -1); }

  function exampleFlag(p, isEx) {
    if (isEx) p.is_example = true;
    return p;
  }

  /** True when the event came from inside the cross-check panel. */
  function inXc(t) {
    var el = t;
    while (el && el !== root) {
      if (el === xcPanel) return true;
      el = el.parentNode;
    }
    return false;
  }

  function setCode(v) {
    var el = tool.querySelector('input[name="code"]');
    if (el) el.value = v;
  }

  /** Upper case, accents off, punctuation to spaces: how a model name is matched. */
  function normName(s) {
    var n = String(s == null ? "" : s);
    if (typeof n.normalize === "function") {
      try { n = n.normalize("NFD").replace(/[̀-ͯ]/g, ""); } catch (e) { /* keep */ }
    }
    return n.toUpperCase().replace(/[^A-Z0-9]+/g, " ").replace(/^\s+|\s+$/g, "");
  }

  /**
   * A span of years as one finished piece of text, built from the two year
   * keys and nothing else (see yearText). `from` alone is "2007 onwards",
   * `to` alone "until 1999".
   */
  function windowText(from, to) {
    var f = typeof from === "number" && isFinite(from) ? from : null;
    var t = typeof to === "number" && isFinite(to) ? to : null;
    if (f !== null && t !== null && f === t) return yearText({ year: f });
    return yearText({ yearFrom: f, yearTo: t });
  }

  function orList(list) {
    return list.join(" " + T("ambiguous", "altPrefix") + " ");
  }

  // ---------------------------------------------------------------------------
  // "DID YOU MEAN …?" — the engine's confusable suggestions, as chips inside a
  // miss. Never applied automatically: the visitor taps one or does not.
  // ---------------------------------------------------------------------------
  function suggestHtml(res) {
    var sg = res && res.suggestions;
    if (!sg || !sg.length || !STR.suggest) return "";
    if (res.state !== S.INVALID_FORMAT && res.state !== S.UNLISTED_VALUE) return "";
    var chips = [];
    for (var i = 0; i < sg.length && i < 2; i++) {
      var code = String(sg[i].code);
      chips.push('<button type="button" class="sc-chip" data-sc-suggest="' + esc(code) + '" aria-label="' +
        esc(fill(T("suggest", "chipAria"), { code: code })) + '">' + esc(code) + "</button>");
    }
    return '<div class="sc-chips sc-chips--suggest"><span class="sc-chips__label">' + esc(T("suggest", "label")) +
      "</span>" + chips.join("") + "</div>";
  }

  // ---------------------------------------------------------------------------
  // THE GUIDE LINK UNDER A RESULT. It is not built here: the page already
  // server-renders "Real vs Fake guides for {brand}" ([data-sc-guides]), with
  // Shopify's own translated title and locale-correct href on every link, and
  // tags each link with the model ids it covers. This picks one of those:
  // the picked model's guide when there is one, else the default (the brand
  // pillar, or its strongest guide when it has none). Gucci's
  // leather-tab panel keeps the model index's own guide link instead.
  // ---------------------------------------------------------------------------
  function bestGuide() {
    var list = document.querySelector("[data-sc-guides]");
    // A <template>: its links live in .content, not in the element itself.
    if (list && list.content) list = list.content;
    if (!list || !list.querySelectorAll) return null;
    var id = currentModelId();
    if (id) {
      var links = list.querySelectorAll("a[data-sc-guide-models]");
      for (var i = 0; i < links.length; i++) {
        if ((" " + links[i].getAttribute("data-sc-guide-models") + " ").indexOf(" " + id + " ") !== -1) return links[i];
      }
    }
    return list.querySelector("a[data-sc-guide-default]");
  }

  function guideHtml(res) {
    if (!res || isMissState(res.state) || !STR.guides) return "";
    if (root.getAttribute("data-model-index")) return "";
    var a = bestGuide();
    if (!a) return "";
    var title = String(a.textContent || "").replace(/\s+/g, " ").replace(/^\s+|\s+$/g, "");
    var href = a.getAttribute("href");
    if (!title || !href) return "";
    return '<p class="sc-result-guide"><a class="sc-result-guide__link" href="' + esc(href) + '">' +
      fillEsc(T("guides", "cardLink"), { title: title }) + "</a></p>";
  }

  // ---------------------------------------------------------------------------
  // THE ROUTE UNDER A MISS. (Anton, 2026-10-27.) When this page cannot read
  // what was typed, and the page names one that reads a likely neighbour (the
  // watch reference reader from Omega, the sunglasses checker from Prada), the
  // card ends with that link, styled like the guide link. The server renders
  // it in a <template data-sc-route> with its translated text and locale href
  // (buildTheme routeTemplate); this only clones it. It replaced a loose line of
  // small text under the Check button that floated with nothing tying it to
  // the answer. Not on a miss we could not read at all (non-Latin input), and
  // not when another panel on this page reads the code (otherTool).
  // ---------------------------------------------------------------------------
  function routeHtml() {
    var tpl = document.querySelector("template[data-sc-route]");
    var a = tpl && tpl.content ? tpl.content.querySelector("a") : null;
    if (!a) return "";
    var text = String(a.textContent || "").replace(/\s+/g, " ").replace(/^\s+|\s+$/g, "");
    var href = a.getAttribute("href");
    if (!text || !href) return "";
    return '<p class="sc-result-guide sc-result-route"><a class="sc-result-guide__link" href="' + esc(href) + '">' +
      esc(text) + "</a></p>";
  }

  // ---------------------------------------------------------------------------
  // THE MAIN RENDER, with the cross-check applied.
  // ---------------------------------------------------------------------------
  function computeXc(res) {
    if (!xcOn || !xcPanel || !res || isMissState(res.state) || res.state === S.OUT_OF_SCOPE) return null;
    var item = readItem();
    var any = false;
    for (var k in item) if (Object.prototype.hasOwnProperty.call(item, k)) any = true;
    if (!any) return null;
    try {
      return SerialDecoder.crossCheck(res, item, rules, modelHit ? { modelIndexHit: modelHit } : {});
    } catch (e) {
      return null;
    }
  }

  function renderMain() {
    var res = lastRes;
    if (!res) return;
    var xc = computeXc(res);
    var view = null;
    // EXACTLY ONE reading left out of several: that one is THE answer now.
    if (xc && xc.narrowed && xc.narrowed.length === 1 && res.candidates && res.candidates.length > 1) {
      var keep = xc.narrowed[0];
      var others = [];
      for (var i = 0; i < res.candidates.length; i++) {
        if (res.candidates[i] !== keep) others.push(describe(res.candidates[i]));
      }
      var byModel = false;
      for (var j = 0; j < xc.checks.length; j++) {
        if (xc.checks[j].type === "model" && xc.checks[j].status === "fits") byModel = true;
      }
      view = { cand: keep, by: byModel ? "model" : "item", alt: orList(others) };
    }
    render(res, lastRaw, view);
    showExtras(res);
    renderXc(xc);
  }

  function showExtras(res) {
    var ok = !!(res && !isMissState(res.state));
    if (actionsEl) actionsEl.hidden = !ok;
    if (xcPanel) xcPanel.hidden = !(ok && res.state !== S.OUT_OF_SCOPE);
    if (!ok || !xcPanel || xcPanel.hidden) setConflictCta(false);
  }

  // ---------------------------------------------------------------------------
  // EXAMPLE CHIPS. "Try an example:" and two to four codes taken from the
  // ruleset's own era examples, one per era, each one checked to decode before
  // it is offered. A chip run is flagged is_example on every event and never
  // marks the cart, so GA4 lookups stay the visitors' own codes.
  // ---------------------------------------------------------------------------
  /** The enum inputs of this panel, as {name: [values]}. */
  function enumValues() {
    var out = {};
    var ins = rules.inputs || [];
    for (var i = 0; i < ins.length; i++) {
      if (ins[i].type !== "enum" || !ins[i].options) continue;
      out[ins[i].name] = [];
      for (var o = 0; o < ins[i].options.length; o++) out[ins[i].name].push(String(ins[i].options[o].value));
    }
    return out;
  }

  /**
   * An era's `example` as something the tool can run: the code, plus any
   * answer the era needs to its other questions. "circle T = 1990" is the code
   * T with the shape answered "circle"; a Gucci two-row example carries the
   * layout its era `requires`; a question nothing names is answered "unknown"
   * where the ruleset offers that option, so the example shows every reading.
   */
  function exampleOf(era, enums) {
    // `exampleChip: false` marks an era answered by a question, not a typed
    // code (Burberry's label eras): its example names a label, so as a chip it
    // put the word "label" into the code field (live check 2026-10-28).
    if (!era || era.example == null || era.exampleChip === false) return null;
    var t = String(era.example).split(/\s*(?:\u2192|->|=|,|;|\s+or\s+)\s*/)[0].replace(/^\s+|\s+$/g, "");
    if (!t || t.length > 24) return null;
    var extra = {};
    var words = t.split(/\s+/);
    var keep = [];
    for (var w = 0; w < words.length; w++) {
      var hit = false;
      for (var name in enums) {
        if (!Object.prototype.hasOwnProperty.call(enums, name)) continue;
        if (words.length > 1 && enums[name].indexOf(words[w].toLowerCase()) !== -1) { extra[name] = words[w].toLowerCase(); hit = true; }
      }
      if (!hit) keep.push(words[w]);
    }
    var code = keep.join(" ");
    if (!code) return null;
    var req = era.requires || {};
    for (var rk in req) {
      if (!Object.prototype.hasOwnProperty.call(req, rk) || extra[rk] != null) continue;
      var rv = Array.isArray(req[rk]) ? req[rk][0] : req[rk];
      if (rv != null) extra[rk] = String(rv);
    }
    var ins = rules.inputs || [];
    for (var i = 0; i < ins.length; i++) {
      var inp = ins[i];
      if (inp.type !== "enum" || !inp.required || extra[inp.name] != null) continue;
      var vals = enums[inp.name] || [];
      // "other" is Burberry's "Something else, or I can't tell": like
      // "unknown", it answers without claiming a label the chip never showed.
      extra[inp.name] = vals.indexOf("unknown") !== -1 ? "unknown" : vals.indexOf("other") !== -1 ? "other" : vals[0];
    }
    return { code: code, extra: extra };
  }

  function encodeExtra(extra) {
    var parts = [];
    for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) parts.push(enc(k) + "=" + enc(extra[k]));
    return parts.join("&");
  }

  /** Set the panel's other questions from "name=value&…" (an example chip). */
  function applyExtra(str) {
    if (!str) return;
    var pairs = String(str).split("&");
    var els = fields();
    for (var p = 0; p < pairs.length; p++) {
      var kv = pairs[p].split("=");
      var name = decodeURIComponent(kv[0] || "");
      var val = decodeURIComponent(kv[1] || "");
      for (var i = 0; i < els.length; i++) {
        if (els[i].name !== name) continue;
        if (els[i].type === "radio") els[i].checked = els[i].value === val;
        else els[i].value = val;
      }
    }
  }

  function buildExamples() {
    if (!STR.examples || !go || !go.parentNode || typeof go.parentNode.insertBefore !== "function") return;
    if (!tool.querySelector('input[name="code"]')) return;
    var enums = enumValues();
    var seen = {};
    var list = [];
    var eras = rules.eras || [];
    for (var i = 0; i < eras.length && list.length < 4; i++) {
      var ex = exampleOf(eras[i], enums);
      if (!ex) continue;
      var vals = { code: ex.code };
      for (var k in ex.extra) if (Object.prototype.hasOwnProperty.call(ex.extra, k)) vals[k] = ex.extra[k];
      if (!ready(vals)) continue;
      var r;
      try { r = SerialDecoder.decode(vals, rules); } catch (e) { continue; }
      if (isMissState(r.state) || r.state === S.OUT_OF_SCOPE || r.state === S.NO_CODE_ERA) continue;
      var key = String(r.input || ex.code).toUpperCase();
      if (seen[key]) continue;
      seen[key] = true;
      list.push(ex);
    }
    // One is enough when the ruleset only documents one shape (Chanel's single
    // numeric run): an empty row on that one page read as a missing feature.
    if (!list.length) return;
    var chips = [];
    for (var c = 0; c < list.length; c++) {
      chips.push('<button type="button" class="sc-chip sc-chip--link" data-sc-example="' + esc(list[c].code) + '" data-sc-example-extra="' +
        esc(encodeExtra(list[c].extra)) + '" aria-label="' + esc(fill(T("examples", "chipAria"), { code: list[c].code })) + '">' +
        esc(list[c].code) + "</button>");
    }
    var wrap = document.createElement("div");
    wrap.className = "sc-chips sc-chips--examples";
    wrap.innerHTML = '<span class="sc-chips__label">' + esc(T("examples", "label")) + "</span>" + chips.join("");
    go.parentNode.insertBefore(wrap, go.nextSibling);
    fitExamples(wrap);
    var queued = false;
    var refit = function () {
      if (queued) return;
      queued = true;
      (window.requestAnimationFrame || setTimeout)(function () { queued = false; fitExamples(wrap); });
    };
    if (typeof window.addEventListener === "function") window.addEventListener("resize", refit);
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === "function") document.fonts.ready.then(refit);
  }

  // On a phone the example row stays on the "Try an example:" line (Anton,
  // 2026-10-28): chips that would wrap onto a second line are hidden, and the
  // first chip always stays, even when it is too long to share the line.
  function fitExamples(wrap) {
    var label = wrap.querySelector && wrap.querySelector(".sc-chips__label");
    var chips = wrap.querySelectorAll ? wrap.querySelectorAll(".sc-chip") : [];
    if (!label || !chips.length || typeof label.getBoundingClientRect !== "function") return;
    for (var i = 0; i < chips.length; i++) chips[i].style.display = "";
    if (!window.matchMedia || !window.matchMedia("(max-width: 640px)").matches) return;
    var lr = label.getBoundingClientRect();
    var line = lr.top + lr.height / 2;
    // Once one chip wraps, every later one goes too, so a shorter chip never
    // jumps ahead of a longer one and the examples keep their order.
    var full = false;
    for (var j = 1; j < chips.length; j++) {
      if (!full) {
        var cr = chips[j].getBoundingClientRect();
        full = Math.abs(cr.top + cr.height / 2 - line) > lr.height / 2;
      }
      if (full) chips[j].style.display = "none";
    }
  }

  // ---------------------------------------------------------------------------
  // THE SHARE LINK. "Copy link to this result" copies this page with the
  // result in the FRAGMENT: #code=SF2134&model=neverfull. Analytics and the
  // server never see a fragment, so the typed code still never reaches either;
  // and a fragment is not a new URL to a crawler, so every shared link still
  // points its backlink at the canonical page. Never the query string.
  // ---------------------------------------------------------------------------
  function buildActions() {
    if (!STR.share || !out.parentNode || typeof out.parentNode.insertBefore !== "function") return;
    actionsEl = document.createElement("div");
    actionsEl.className = "sc-actions";
    actionsEl.hidden = true;
    actionsEl.innerHTML =
      '<button type="button" class="sc-share" data-sc-share><span class="sc-share__icon" aria-hidden="true"></span>' +
      '<span class="sc-share__text">' + esc(T("share", "button")) + "</span></button>" +
      '<span class="sc-share__status" role="status" aria-live="polite"></span>';
    // To the RIGHT of the primary "Check code" button (Anton, 2026-10-27), not
    // under the result: one row, the share button appearing once there is a
    // result to share. On a phone it collapses to its icon (CSS). Falls back
    // to under the result card when the page has no check button.
    if (go && go.parentNode && typeof go.parentNode.insertBefore === "function") {
      var row = document.createElement("div");
      row.className = "sc-go-row";
      go.parentNode.insertBefore(row, go);
      row.appendChild(go);
      row.appendChild(actionsEl);
    } else {
      out.parentNode.insertBefore(actionsEl, out.nextSibling);
    }
    shareStatus = actionsEl.querySelector(".sc-share__status");
  }

  function enc(v) { return encodeURIComponent(String(v)); }

  /** The query string a result writes: the panel, when it is not the
   *  default one, then every field. Live since batch 3x (2026-10-21). */
  function resultQs() {
    // `readFields()`, never `fields`: `fields` is the element-list HELPER
    // FUNCTION, so `for (var k in fields)` enumerated a function's own
    // properties (always none), every call wrote a bare pathname, and that
    // silently DELETED the query string a shared link arrived with.
    var qs = [];
    if (group && toolId && toolId !== group.getAttribute("data-sc-tools")) qs.push("tool=" + enc(toolId));
    // A stacked panel other than the first names itself too, so the copied
    // link restores the panel that answered and nothing else.
    if (!group && toolId && stackedPanels(brand).indexOf(root) > 0) qs.push("tool=" + enc(toolId));
    var vals = readFields();
    for (var k in vals) {
      if (Object.prototype.hasOwnProperty.call(vals, k) && vals[k]) qs.push(enc(k) + "=" + enc(vals[k]));
    }
    return qs;
  }

  /** Round 3: the cross-check answers, for the FRAGMENT. Never the code. */
  function itemParts() {
    var parts = [];
    var it = readItem();
    if (it.model) parts.push("model=" + enc(it.model));
    if (it.madeIn) parts.push("made=" + enc(it.madeIn));
    if (it.claimedYear) parts.push("year=" + enc(it.claimedYear));
    if (pairListMode) {
      for (var p in it.pairCode || {}) if (Object.prototype.hasOwnProperty.call(it.pairCode, p)) parts.push("pair-" + enc(p) + "=" + enc(it.pairCode[p]));
      for (var q in it.pairCodeOther || {}) if (Object.prototype.hasOwnProperty.call(it.pairCodeOther, q)) parts.push("pair2-" + enc(q) + "=" + enc(it.pairCodeOther[q]));
    } else {
      if (it.pairCode) parts.push("pair=" + enc(it.pairCode));
      if (it.pairCodeOther) parts.push("pair2=" + enc(it.pairCodeOther));
    }
    if (it.features) {
      var fs = [];
      for (var f in it.features) if (Object.prototype.hasOwnProperty.call(it.features, f)) fs.push(f);
      if (fs.length) parts.push("feat=" + enc(fs.join(",")));
    }
    return parts.join("&");
  }

  function itemHash() {
    var p = itemParts();
    return p ? "#" + p : "";
  }

  function writeResultUrl() {
    if (!lastRes || !window.history || !window.history.replaceState) return;
    try {
      window.history.replaceState(null, "", urlWith(resultQs()) + itemHash());
    } catch (e) { /* the result still stands */ }
  }

  /** The link the share button copies: the canonical path, this result's own
   *  query, the cross-check in the fragment. Not urlWith(): a visitor's gclid
   *  and utm_* tags belong to their visit, not to everyone they send it to. */
  function shareUrl() {
    var qs = resultQs();
    return (location.protocol ? location.protocol + "//" + location.host : "") + location.pathname +
      (qs.length ? "?" + qs.join("&") : "") + itemHash();
  }

  /** The fragment as parameters, or null. `withFields`: only when it also
   *  carries one of this panel's fields (a round-3 style #code= link). */
  function readFragment(withFields) {
    var h = String(location.hash || "").replace(/^#/, "");
    if (!h || h.indexOf("=") === -1) return null;
    var p;
    try { p = new URLSearchParams(h); } catch (e) { return null; }
    if (!withFields) return p;
    var els = fields();
    for (var i = 0; i < els.length; i++) if (p.get(els[i].name) != null) return p;
    return null;
  }

  function legacyCopy(text) {
    try {
      var ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return !!ok;
    } catch (e) {
      return false;
    }
  }

  function copyLink() {
    var url = shareUrl();
    writeResultUrl();
    function done(ok) {
      if (!shareStatus) return;
      shareStatus.textContent = T("share", ok ? "copied" : "failed");
      // The phone layout shows only the icon, so success also swaps it to a tick.
      if (ok && actionsEl) actionsEl.className = "sc-actions is-copied";
      if (shareTimer) clearTimeout(shareTimer);
      shareTimer = setTimeout(function () {
        shareStatus.textContent = "";
        if (actionsEl) actionsEl.className = "sc-actions";
      }, 4000);
    }
    try {
      if (window.navigator && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        navigator.clipboard.writeText(url).then(function () { done(true); }, function () { done(legacyCopy(url)); });
        return;
      }
    } catch (e) { /* fall through */ }
    done(legacyCopy(url));
  }

  // ---------------------------------------------------------------------------
  // "DOES THE CODE FIT THE ITEM?" — the cross-check panel.
  //
  // Collapsed by default, under a result, with a one-line teaser. The fields
  // are whatever crossCheckOptions() offers for this ruleset; the UI never
  // reads the ruleset's crossCheck block for anything but the pair-code SHAPE.
  // Every row says ✓ fits, ! doesn't fit or ? can't tell, in a sentence from
  // ui-strings.json `crossCheck`. It never says fake: a conflict carries the
  // caveat, and the CTA under it is the one honest next step.
  // ---------------------------------------------------------------------------
  function xcField(id, label, control, extraCls) {
    return '<div class="sc-xc__field' + (extraCls ? " " + extraCls : "") + '"><label class="pv5-decoder__prompt" for="' +
      esc(id) + '">' + esc(label) + "</label>" + control + "</div>";
  }

  function pairLabel(id, suffix, fallbackKey) {
    var map = T("crossCheck", "pairLabels") || {};
    var key = (id || "") + (suffix || "");
    return (typeof map === "object" && map[key]) ? map[key] : T("crossCheck", fallbackKey);
  }

  function featureLabel(id, given) {
    var map = T("crossCheck", "features") || {};
    if (typeof map === "object" && map[id]) return map[id];
    return given || T("crossCheck", "featureFallback");
  }

  function buildXc() {
    if (!xcOn || !out.parentNode || typeof out.parentNode.insertBefore !== "function") return;
    var uid = "sc-xc-" + String(root.getAttribute("data-tool") || brand).replace(/[^A-Za-z0-9_-]/g, "");
    var h = [];
    h.push('<summary class="sc-xc__summary"><span class="sc-xc__icon" aria-hidden="true"></span>' +
      '<span class="sc-xc__head"><span class="sc-xc__title">' + esc(T("crossCheck", "title")) + "</span>" +
      '<span class="sc-xc__teaser">' + esc(T("crossCheck", "teaser")) + "</span></span>" +
      '<span class="sc-xc__chev" aria-hidden="true"></span></summary>');
    h.push('<div class="sc-xc__body"><div class="sc-xc__fields">');
    if (has("model")) {
      var opts = [];
      for (var m = 0; m < XO.models.length; m++) opts.push('<option value="' + esc(XO.models[m].name) + '"></option>');
      h.push(xcField(uid + "-model", T("crossCheck", "fieldModel"),
        '<div class="pv5-decoder__control"><input id="' + uid + '-model" class="pv5-decoder__input sc-xc__input" type="text" list="' +
        uid + '-models" data-xc="model" autocomplete="off" autocapitalize="words" spellcheck="false" placeholder="' +
        esc(fill(T("crossCheck", "modelPlaceholder"), { example: XO.models[0].name })) + '"><datalist id="' + uid + '-models">' +
        opts.join("") + '</datalist><span class="pv5-decoder__select-icon material-symbols-outlined" aria-hidden="true">expand_more</span></div>',
        "sc-xc__field--wide"));
    }
    if (has("madeIn")) {
      var cs = ['<option value="">' + esc(T("crossCheck", "madeInNone")) + "</option>"];
      for (var c = 0; c < XO.countries.length; c++) {
        cs.push('<option value="' + esc(XO.countries[c]) + '">' + esc(factoryText(XO.countries[c])) + "</option>");
      }
      h.push(xcField(uid + "-made", T("crossCheck", "fieldMadeIn"),
        '<div class="pv5-decoder__control"><select id="' + uid + '-made" class="pv5-decoder__select sc-xc__input" data-xc="madeIn">' +
        cs.join("") + '</select><span class="pv5-decoder__select-icon material-symbols-outlined" aria-hidden="true">expand_more</span></div>'));
    }
    if (has("claimedYear")) {
      h.push(xcField(uid + "-year", T("crossCheck", "fieldYear"),
        '<div class="pv5-decoder__control"><input id="' + uid + '-year" class="pv5-decoder__input sc-xc__input" type="text" inputmode="numeric" maxlength="4" autocomplete="off" data-xc="claimedYear" placeholder="' +
        esc(fill(T("crossCheck", "yearPlaceholder"), { example: 2015 })) + '"></div>'));
    }
    if (has("pairCode")) {
      for (var p = 0; p < XO.pairCodes.length; p++) {
        var pc = XO.pairCodes[p];
        var pid = pc.id || "";
        var base = uid + "-pair" + p;
        var attrs = ' data-xc-id="' + esc(pid) + '" autocomplete="off" autocapitalize="characters" spellcheck="false"';
        if (pc.compare === "pair") {
          h.push(xcField(base + "a", pairLabel(pid, "__other", "fieldPairFirst"),
            '<div class="pv5-decoder__control"><input id="' + base + 'a" class="pv5-decoder__input sc-xc__input" type="text" data-xc="pairOther"' + attrs + "></div>"));
          h.push(xcField(base + "b", pairLabel(pid, "", "fieldPairSecond"),
            '<div class="pv5-decoder__control"><input id="' + base + 'b" class="pv5-decoder__input sc-xc__input" type="text" data-xc="pair"' + attrs + "></div>"));
        } else {
          h.push(xcField(base, pairLabel(pid, "", pc.compare === "group" ? "fieldPairMeasured" : "fieldPairCode"),
            '<div class="pv5-decoder__control"><input id="' + base + '" class="pv5-decoder__input sc-xc__input" type="text"' +
            (pc.rule === "numeric" ? ' inputmode="decimal"' : "") + ' data-xc="pair"' + attrs + "></div>"));
        }
      }
    }
    if (has("feature")) {
      var boxes = [];
      for (var f = 0; f < XO.features.length; f++) {
        var fid = XO.features[f].id;
        boxes.push('<label class="sc-xc__check"><input type="checkbox" class="sc-xc__box" data-xc-feature="' + esc(fid) + '">' +
          '<span class="sc-xc__tick" aria-hidden="true"></span><span>' + esc(featureLabel(fid, XO.features[f].label)) + "</span></label>");
      }
      h.push('<fieldset class="sc-xc__field sc-xc__field--wide sc-xc__features"><legend class="pv5-decoder__prompt">' +
        esc(T("crossCheck", "fieldFeatures")) + "</legend>" + boxes.join("") + "</fieldset>");
    }
    h.push("</div>");
    // THE OUTCOME GROUP: the rows, the narrowing line, the caveat and the CTA as ONE
    // group under a hairline, on the result card's type scale (Anton,
    // 2026-10-27). Hidden until there is a row to show, so the hairline never
    // sits under an empty form.
    h.push('<div class="sc-xc__outcome" data-xc-outcome hidden>');
    h.push('<ul class="sc-xc__checks" data-xc-checks aria-live="polite"></ul>');
    h.push('<p class="sc-xc__narrowed" data-xc-narrowed hidden></p>');
    h.push('<p class="sc-xc__caveat" data-xc-caveat hidden>' + esc(T("crossCheck", "caveat")) + "</p>");
    h.push('<a class="sc-xc__cta" data-xc-cta data-checker-cta="xcheck-cta" href="#" hidden><span>' +
      esc(T("crossCheck", "ctaConflict")) + '</span><span class="sc-xc__cta-arrow" aria-hidden="true"></span></a>');
    h.push("</div></div>");

    xcPanel = document.createElement("details");
    xcPanel.className = "sc-xc";
    xcPanel.setAttribute("data-sc-xc", "");
    xcPanel.hidden = true;
    xcPanel.innerHTML = h.join("");
    var anchor = actionsEl && actionsEl.parentNode && actionsEl.parentNode.className !== "sc-go-row" ? actionsEl : out;
    anchor.parentNode.insertBefore(xcPanel, anchor.nextSibling);

    xcPanel.addEventListener("input", function (ev) {
      var t = ev.target;
      if (t && t.getAttribute && t.getAttribute("data-xc") === "model") xcCommittedModel = false;
      scheduleXc(220);
    });
    xcPanel.addEventListener("change", function (ev) {
      var t = ev.target;
      if (t && t.getAttribute && t.getAttribute("data-xc") === "model") xcCommittedModel = true;
      scheduleXc(0);
    });
    xcPanel.addEventListener("keydown", function (ev) {
      var t = ev.target;
      if ((ev.key === "Enter" || ev.keyCode === 13) && t && t.tagName === "INPUT") {
        ev.preventDefault();
        if (t.getAttribute("data-xc") === "model") xcCommittedModel = true;
        scheduleXc(0);
      }
    });
  }

  function scheduleXc(ms) {
    if (xcInputTimer) clearTimeout(xcInputTimer);
    xcInputTimer = setTimeout(renderMain, ms);
  }

  function modelIdFor(text) {
    if (!XO || !XO.models) return "";
    var n = normName(text);
    if (!n) return "";
    for (var i = 0; i < XO.models.length; i++) {
      if (normName(XO.models[i].name) === n || XO.models[i].id === text) return XO.models[i].id;
    }
    return "";
  }

  function currentModelId() {
    if (!xcPanel) return "";
    var el = xcPanel.querySelector('[data-xc="model"]');
    return el ? modelIdFor(el.value) : "";
  }

  /**
   * The item as the panel describes it, in crossCheck()'s shape. A half-typed
   * model is NOT sent: "Nev" would come back "we have no years for that model"
   * on every keystroke. A name from the list is sent as its id at once; free
   * text only once committed (Enter, blur, or a pick), when the engine can
   * still match it against the model's aliases.
   */
  function readItem() {
    var it = {};
    if (!xcPanel) return it;
    var q = function (sel) { return xcPanel.querySelector(sel); };
    var m = q('[data-xc="model"]');
    if (m && m.value.replace(/\s+/g, "")) {
      var id = modelIdFor(m.value);
      if (id) it.model = id;
      else if (xcCommittedModel) it.model = m.value.replace(/^\s+|\s+$/g, "");
    }
    var c = q('[data-xc="madeIn"]');
    if (c && c.value) it.madeIn = c.value;
    var y = q('[data-xc="claimedYear"]');
    var yv = y ? String(y.value || "").replace(/[^0-9]/g, "") : "";
    if (yv.length === 4) it.claimedYear = parseInt(yv, 10);
    var pairs = xcPanel.querySelectorAll("[data-xc-id]");
    for (var i = 0; i < pairs.length; i++) {
      var el = pairs[i];
      var val = String(el.value || "").replace(/^\s+|\s+$/g, "");
      if (!val) continue;
      var slot = el.getAttribute("data-xc") === "pairOther" ? "pairCodeOther" : "pairCode";
      if (pairListMode) {
        it[slot] = it[slot] || {};
        it[slot][el.getAttribute("data-xc-id")] = val;
      } else {
        it[slot] = val;
      }
    }
    var boxes = xcPanel.querySelectorAll("[data-xc-feature]");
    for (var b = 0; b < boxes.length; b++) {
      if (!boxes[b].checked) continue;
      it.features = it.features || {};
      it.features[boxes[b].getAttribute("data-xc-feature")] = true;
    }
    return it;
  }

  function restoreItem(p) {
    if (!xcPanel) return;
    var any = false;
    var q = function (sel) { return xcPanel.querySelector(sel); };
    var mv = p.get("model");
    var m = q('[data-xc="model"]');
    if (mv && m) {
      var name = mv;
      for (var i = 0; i < XO.models.length; i++) if (XO.models[i].id === mv) name = XO.models[i].name;
      m.value = name;
      xcCommittedModel = true;
      any = true;
    }
    var made = p.get("made");
    var c = q('[data-xc="madeIn"]');
    if (made && c) { c.value = made; any = any || c.value === made; }
    var yr = p.get("year");
    var y = q('[data-xc="claimedYear"]');
    if (yr && y) { y.value = String(yr).replace(/[^0-9]/g, "").slice(0, 4); any = true; }
    var pairs = xcPanel.querySelectorAll("[data-xc-id]");
    for (var k = 0; k < pairs.length; k++) {
      var el = pairs[k];
      var other = el.getAttribute("data-xc") === "pairOther";
      var key = (other ? "pair2" : "pair") + (pairListMode ? "-" + el.getAttribute("data-xc-id") : "");
      var pv = p.get(key);
      if (pv) { el.value = pv; any = true; }
    }
    var feat = p.get("feat");
    if (feat) {
      var ids = feat.split(",");
      var boxes = xcPanel.querySelectorAll("[data-xc-feature]");
      for (var b = 0; b < boxes.length; b++) {
        if (ids.indexOf(boxes[b].getAttribute("data-xc-feature")) !== -1) { boxes[b].checked = true; any = true; }
      }
    }
    if (any) xcPanel.open = true;
  }

  /** The {placeholders} one check's sentence can use. */
  function xcVars(ck) {
    var p = ck.params || {};
    var v = baseVars(null);
    v.model = p.model || "";
    v.indexModel = p.indexModel || "";
    v.indexMatch = p.indexMatch || "";
    v.window = windowText(p.from, p.to);
    v.codeYears = windowText(p.codeFrom, p.codeTo);
    v.country = p.country ? factoryText(p.country) : "";
    var cs = [];
    for (var i = 0; i < (p.countries || []).length; i++) cs.push(factoryText(p.countries[i]));
    v.countries = orList(cs);
    v.claimedYear = p.claimedYear != null ? yearText({ year: p.claimedYear }) : "";
    v.endedYear = p.endedYear != null ? yearText({ year: p.endedYear }) : "";
    v.replacedBy = p.replacedBy || "";
    v.feature = featureLabel(p.featureId, p.feature);
    v.impliedWindow = windowText(p.impliedFrom, p.impliedTo);
    v.pairCode = p.pairCode != null ? p.pairCode : "";
    v.code = p.code != null ? p.code : "";
    v.label = p.label || "";
    return v;
  }

  function setConflictCta(on) {
    if (!stickyEl) {
      stickyEl = document.querySelector("[data-sc-sticky] .mobile-sticky-cta__title");
      if (stickyEl) stickyText = stickyEl.textContent;
    }
    if (stickyEl && stickyText != null) stickyEl.textContent = on ? T("crossCheck", "ctaConflict") : stickyText;
  }

  function renderXc(xc) {
    if (!xcPanel) return;
    var list = xcPanel.querySelector("[data-xc-checks]");
    var narrowEl = xcPanel.querySelector("[data-xc-narrowed]");
    var caveat = xcPanel.querySelector("[data-xc-caveat]");
    var cta = xcPanel.querySelector("[data-xc-cta]");
    var rows = [];
    var checks = (xc && xc.checks) || [];
    for (var i = 0; i < checks.length; i++) {
      var ck = checks[i];
      var exc = "";
      if (ck.params && ck.params.exception) {
        var em = T("crossCheck", "exceptions") || {};
        if (typeof em === "object" && em[ck.params.exception]) exc = " " + esc(em[ck.params.exception]);
      }
      rows.push('<li class="sc-xc__row sc-xc__row--' + esc(ck.status) + '"><span class="sc-xc__mark" aria-hidden="true"></span>' +
        '<span class="sc-xc__text">' + fillEsc(T("crossCheck", ck.key), xcVars(ck)) + exc + "</span></li>");
    }
    var html = rows.join("");
    if (html !== xcHtml) {
      xcHtml = html;
      if (list) list.innerHTML = html;
    }
    var many = xc && xc.narrowed && xc.narrowed.length > 1 ? xc.narrowed : null;
    if (narrowEl) {
      narrowEl.hidden = !many;
      if (many) narrowEl.textContent = fill(T("crossCheck", "narrowedMany"), { years: orList(many.map(describe)) });
    }
    var outcomeEl = xcPanel.querySelector("[data-xc-outcome]");
    if (outcomeEl) outcomeEl.hidden = !rows.length && !many;
    var conflict = !!(xc && xc.overall === "conflict");
    if (caveat) caveat.hidden = !conflict;
    if (cta) {
      cta.hidden = !conflict;
      var src = document.querySelector('[data-checker-cta="tier-cta"]') || document.querySelector('.sc-page a[href*="/products/"]');
      if (src && src.getAttribute("href")) cta.setAttribute("href", src.getAttribute("href"));
    }
    setConflictCta(conflict);
    queueXcEvent(xc);
  }

  /**
   * GA4 `checker_crosscheck` and the `lg_xcheck` cart mark, settled rather
   * than per keystroke. Field TYPES and the outcome only: never the code,
   * never the model, never a year.
   */
  function queueXcEvent(xc) {
    if (!xc || !xc.overall) return;
    var sig = xc.fieldsUsed + "|" + xc.overall + "|" + (xc.narrowed ? 1 : 0);
    if (sig === xcSent) return;
    if (xcEventTimer) clearTimeout(xcEventTimer);
    var isEx = lastRunExample;
    xcEventTimer = setTimeout(function () {
      xcSent = sig;
      track("checker_crosscheck", exampleFlag({
        checker_brand: brand,
        fields_used: xc.fieldsUsed,
        overall: xc.overall,
        narrowed: !!xc.narrowed
      }, isEx));
      if (!isEx) markXcheck(xc.overall);
      writeResultUrl();
    }, 900);
  }

  function initRound3() {
    try { buildExamples(); } catch (e1) { /* an optional extra must never break the tool */ }
    try { buildActions(); } catch (e2) { actionsEl = null; }
    try { buildXc(); } catch (e3) { xcPanel = null; }
    root.addEventListener("click", function (ev) {
      var el = ev.target;
      while (el && el !== root && el.getAttribute) {
        var sug = el.getAttribute("data-sc-suggest");
        if (sug != null) { setCode(sug); run(true); return; }
        var ex = el.getAttribute("data-sc-example");
        if (ex != null) {
          setCode(ex);
          applyExtra(el.getAttribute("data-sc-example-extra"));
          fromExample = true;
          try { run(true); } finally { fromExample = false; }
          return;
        }
        if (el.getAttribute("data-sc-share") != null) { copyLink(); return; }
        el = el.parentNode;
      }
    });
  }

  // Resolve live, but DEBOUNCED. Typing fired `run` per character and a radio
  // fires `input` and `change` together, so the live region was rewritten on
  // every keystroke and twice per click. 450ms is long enough for a burst of
  // typing to settle and short enough to still feel immediate.
  //
  // `replaceState` rides the same timer deliberately: Safari throttles it at
  // roughly 100 calls per 30 seconds, which a fast typist could reach.
  function schedule() {
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(function () { run(true); }, 450);
  }
  tool.addEventListener("change", function (ev) {
    var t = ev.target;
    // The cross-check panel sits inside the tool but is not one of its inputs:
    // picking a model must not re-run the decode.
    if (inXc(t)) return;
    if (t && t.tagName === "INPUT" && t.type === "text") return;
    schedule();
  });
  var go = root.querySelector("[data-sc-check]");
  if (go) go.addEventListener("click", function () { run(true, true); });
  tool.addEventListener("keydown", function (ev) {
    var t = ev.target;
    if (inXc(t)) return;
    if ((ev.key === "Enter" || ev.keyCode === 13) && t && t.tagName === "INPUT" && t.type === "text") {
      ev.preventDefault();
      run(true, true);
    }
  });

  // The switch's "check it as jewelry" button runs this panel directly.
  root.scRun = function () { run(true); };

  initRound3();



  // ---------------------------------------------------------------------------
  // MODEL LOOKUP
  //
  // The style code is the FIRST SIX characters of the leather-tab serial, and
  // we hold 34,026 of them. The index is ~340KB gzipped, so it is fetched ONCE,
  // lazily, the first time somebody actually looks a code up. It is never part
  // of page load: most visitors read the page and never type anything.
  //
  // A miss is NOT a negative result and must never read as one. Gucci has
  // issued far more codes than any public index holds.
  // ---------------------------------------------------------------------------
  var MODEL_URL = root.getAttribute("data-model-index");
  var modelIndex = null;
  var modelPending = false;
  // The style code the box below the card describes, so render() can put the
  // box back when a cross-check change rewrites the card.
  var modelCode = "";

  function renderModel(code) {
    if (!modelIndex) return;
    paintModel(code);
    // Round 3: the index hit is the strongest cross-check there is ("this code
    // belongs to a Dionysus"), so a new hit re-runs the panel.
    var hit = modelIndex[code];
    var name = hit ? hit[0] : null;
    if (name !== modelHit) {
      modelHit = name;
      renderMain();
    }
  }

  function paintModel(code) {
    if (!modelIndex || !code) return;
    var hit = modelIndex[code];
    var box = out.querySelector("[data-pv5-model]");
    if (!box) {
      box = document.createElement("div");
      box.className = "pv5-decoder__model";
      box.setAttribute("data-pv5-model", "");
      out.appendChild(box);
    }
    // Copy lives in ui-strings.json `model`, like every other sentence on the
    // card. `fillEsc` escapes the values and keeps the <strong> in the template.
    var vars = { code: code, model: hit ? hit[0] : "", brand: rules.displayName };
    if (!hit) {
      box.innerHTML = '<p class="pv5-decoder__model-miss">' +
        fillEsc(T("model", "miss"), vars) + "</p>";
      return;
    }
    var html = '<p class="pv5-decoder__model-hit">' + fillEsc(T("model", "hit"), vars) + "</p>";
    if (hit[1]) {
      html += '<p class="pv5-decoder__model-guide"><a href="' + esc(hit[1]) + '">' +
        esc(hit[2] || T("model", "guideFallback")) + "</a></p>";
    }
    box.innerHTML = html;
  }

  function lookupModel(raw, res) {
    if (!MODEL_URL || !raw) return;
    // Any miss, not only INVALID_FORMAT: an unlisted or impossible reading is
    // no reading, and a style code pulled out of it would be a guess.
    if (isMissState(res.state)) return;

    // ONLY for eras where the style code really IS the leading six digits.
    // The ruleset says which (`modelIndex.lookup.appliesTo`), and honouring it
    // is not optional: an Accessory Collection code "89.02.018" would otherwise
    // be flattened to "890201" and a black-tag "G03374065" to "033740", and
    // either can COLLIDE with a genuine style code and return a confident wrong
    // model. A six-digit run existing is not evidence that it means anything.
    var applies = (rules.modelIndex && rules.modelIndex.lookup &&
                   rules.modelIndex.lookup.appliesTo) || [];
    if (applies.length) {
      var ok = false;
      for (var ai = 0; ai < res.eras.length && !ok; ai++) {
        if (applies.indexOf(res.eras[ai].id) !== -1) ok = true;
      }
      if (!ok) return;
    }

    var m = String(raw).toUpperCase().replace(/[^0-9]/g, "").match(/^([0-9]{6})/);
    if (!m) return;
    var code = m[1];
    modelCode = code;
    if (modelIndex) { renderModel(code); return; }
    if (modelPending) return;
    modelPending = true;
    var xhr = new XMLHttpRequest();
    xhr.open("GET", MODEL_URL, true);
    xhr.onload = function () {
      modelPending = false;
      if (xhr.status < 200 || xhr.status >= 300) return;   // silent: the decode still stands
      try { modelIndex = JSON.parse(xhr.responseText); } catch (e) { return; }
      renderModel(code);
    };
    xhr.onerror = function () { modelPending = false; };
    xhr.send();
  }

  // Restore a shared or linked result, LAST, so every variable above (the
  // model-index URL included) is assigned before the first run. The code
  // arrives in the query string (?code=T&shape=circle); the cross-check
  // answers, since round 3, in the fragment (#model=lindy). A fragment that
  // carries the code itself is read too.
  (function () {
    // On a switch page only the panel the link names gets the code; the
    // other would answer it with the wrong marking's rules.
    if (group && toolId && activeTool(group) !== toolId) return;
    var frag = readFragment(true);
    var params = frag || new URLSearchParams(location.search || "");
    if (frag) {
      // A fragment names its panel with `tool`; one without a name belongs
      // to the first panel only.
      var wanted = params.get("tool");
      if (!group && (wanted ? wanted !== (toolId || "") : root !== roots[0])) return;
    } else if (!group && toolId && !stackedRestores(root, params, fullRules)) {
      // Stacked panels (Gucci, Omega): only the panel the code fits, or the
      // one `?tool=` names. `fullRules`: each panel applies its own `data-eras`.
      return;
    }
    var any = false;
    var els = fields();
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var v = params.get(el.name);
      if (v == null) continue;
      if (el.type === "radio") {
        if (el.value === v) { el.checked = true; any = true; }
      } else {
        el.value = v;
        any = true;
      }
    }
    var itemP = frag || readFragment(false);
    if (itemP) restoreItem(itemP);
    if (any) run(false);
  })();

  // No FAQ code here any more. The FAQ is guide-v5's `faq-item`, a native
  // <details>/<summary>, so the browser owns the toggle, the keyboard
  // behaviour and the expanded state. The accordion script this file used to
  // carry was reimplementing all three.
  }
})();
